// E:\new-react-project\the-ultimate-guide\src\core-pages\Billing\TeamSetupPayers.js

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { httpsCallable } from "firebase/functions";
// import { doc, getDoc, onSnapshot } from "firebase/firestore";
import { loadStripe } from "@stripe/stripe-js";
import { RefreshCw } from "lucide-react";
import {
  AddressElement,
  CardElement,
  Elements,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";

import { auth, functions } from "../../Security/firebase-config";
import {
  RecaptchaVerifier,
  PhoneAuthProvider,
  linkWithCredential,
  unlink,
} from "firebase/auth";
import { useTeam } from "../../context/TeamContext";
import { useAccess } from "../../context/AccessContext";
import { FUNCTION_NAMES } from "../../utils/functionNames";
import { buildTeamScopedPath, getRequestedTeamId } from "../../utils/teamRouteTarget";
import Footer from "../../components/core-components/Footer";
import TeamCenterFrame from "../../components/team-center/TeamCenterFrame";
import {
  SUBSCRIPTION_PLANS,
  SUBSCRIPTION_PROMO_CONFIG,
  STORAGE_ADD_ON_OPTIONS,
} from "../../config/subscriptionPlans";

import "./TeamSetupPayers.css";

const isDesktopShell = typeof window !== "undefined" && !!window.desktopAPI;
const isDarkMode = isDesktopShell || document.documentElement.classList.contains("dark");
const STRIPE_PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || "";
const stripePromise = STRIPE_PUBLISHABLE_KEY
  ? loadStripe(STRIPE_PUBLISHABLE_KEY).catch((error) => {
      console.warn("[TeamSetupPayers] Stripe.js failed to load:", error);
      return null;
    })
  : Promise.resolve(null);

const FALLBACK_BILLING_COUNTRY_OPTIONS = [
  { value: "CA", label: "Canada" },
  { value: "US", label: "United States" },
  { value: "GB", label: "United Kingdom" },
  { value: "AU", label: "Australia" },
  { value: "DE", label: "Germany" },
  { value: "FR", label: "France" },
];

function buildBillingCountryOptions() {
  if (typeof Intl === "undefined" || typeof Intl.DisplayNames !== "function") {
    return FALLBACK_BILLING_COUNTRY_OPTIONS;
  }

  try {
    const displayNames = new Intl.DisplayNames(["en"], { type: "region" });
    const options = [];

    for (let firstCharCode = 65; firstCharCode <= 90; firstCharCode += 1) {
      for (let secondCharCode = 65; secondCharCode <= 90; secondCharCode += 1) {
        const code = `${String.fromCharCode(firstCharCode)}${String.fromCharCode(secondCharCode)}`;
        const label = displayNames.of(code);

        if (
          typeof label === "string" &&
          label &&
          label !== code &&
          !label.toLowerCase().includes("unknown region")
        ) {
          options.push({ value: code, label });
        }
      }
    }

    if (!options.length) {
      return FALLBACK_BILLING_COUNTRY_OPTIONS;
    }

    return options.sort((left, right) => left.label.localeCompare(right.label));
  } catch {
    return FALLBACK_BILLING_COUNTRY_OPTIONS;
  }
}

const BILLING_COUNTRY_OPTIONS = buildBillingCountryOptions();

const DEFAULT_DESKTOP_BILLING_ADDRESS = Object.freeze({
  name: "",
  country: "CA",
  line1: "",
  line2: "",
  city: "",
  state: "",
  postalCode: "",
});

function getStripeAppearance(darkMode) {
  const textColor = darkMode ? "#F4F8FB" : "#16313C";
  const mutedTextColor = darkMode ? "#99A9B4" : "#6F8290";
  const surfaceColor = darkMode ? "#171F29" : "#F4F8FB";
  const elevatedSurfaceColor = darkMode ? "#1E2834" : "#FFFFFF";
  const borderColor = darkMode ? "rgba(21, 157, 181, 0.28)" : "rgba(22, 49, 60, 0.16)";
  // Stripe renders in its own frame, so pass the resolved central color.
  const focusBorderColor = getComputedStyle(document.documentElement)
    .getPropertyValue("--clr159db5")
    .trim();

  return {
    theme: darkMode ? "night" : "stripe",
    variables: {
      colorPrimary: focusBorderColor,
      colorText: textColor,
      colorTextSecondary: mutedTextColor,
      colorTextPlaceholder: mutedTextColor,
      colorBackground: surfaceColor,
      colorDanger: "#dd6262",
      borderRadius: "12px",
      spacingUnit: "4px",
    },
    rules: {
      ".Label": {
        color: mutedTextColor,
        fontWeight: "700",
      },
      ".Input": {
        backgroundColor: elevatedSurfaceColor,
        borderColor,
        boxShadow: "none",
        color: textColor,
      },
      ".Input::placeholder": {
        color: mutedTextColor,
      },
      ".Input:focus": {
        borderColor: focusBorderColor,
        boxShadow: `0 0 0 1px ${focusBorderColor}`,
      },
      ".Input--invalid": {
        borderColor: "#dd6262",
        boxShadow: "none",
      },
      ".Dropdown": {
        backgroundColor: elevatedSurfaceColor,
        borderColor,
        color: textColor,
      },
      ".Dropdown:focus": {
        borderColor: focusBorderColor,
        boxShadow: `0 0 0 1px ${focusBorderColor}`,
      },
      ".Block": {
        backgroundColor: surfaceColor,
        borderColor: "transparent",
        boxShadow: "none",
      },
      ".Tab": {
        backgroundColor: elevatedSurfaceColor,
        borderColor,
        color: textColor,
      },
      ".Tab:hover": {
        color: textColor,
      },
      ".Tab--selected": {
        backgroundColor: surfaceColor,
        borderColor: focusBorderColor,
        color: textColor,
        boxShadow: `0 0 0 1px ${focusBorderColor}`,
      },
      ".Error": {
        color: "#ffbcbc",
      },
    },
  };
}

const SUPPORTED_CURRENCIES = ["CAD", "USD", "EUR", "GBP"];
const FULL_TEAM_MODE_THRESHOLD = 5;
const FULL_TEAM_MODE_PRICE_CAP_USERS = 5;
const FREE_TRIAL_DAYS = 7;

function normalizePendingChangeTiming(value) {
  return value === "immediate_prorated" ? "immediate_prorated" : "next_cycle";
}

function getResolvedBillingCurrency(billingSummary, billing) {
  const summaryCurrency = getNormalizedString(billingSummary?.billingCurrency);
  const billingCurrency = getNormalizedString(billing?.billingCurrency);

  const resolved = (summaryCurrency || billingCurrency || "CAD").toUpperCase();

  return SUPPORTED_CURRENCIES.includes(resolved) ? resolved : "CAD";
}

function formatMoneyAmount(amount, currency) {
  if (typeof amount !== "number" || !Number.isFinite(amount)) {
    return "—";
  }

  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    currencyDisplay: "code",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function coerceBillingDate(value) {
  if (!value) return null;

  const d =
    typeof value?.toDate === "function"
      ? value.toDate()
      : typeof value?._seconds === "number"
        ? new Date(value._seconds * 1000)
        : typeof value?.seconds === "number"
          ? new Date(value.seconds * 1000)
          : typeof value === "number"
            ? new Date(value * 1000)
            : value instanceof Date
              ? value
              : typeof value === "string"
                ? new Date(value)
                : null;

  return d && !Number.isNaN(d.getTime()) ? d : null;
}

function formatBillingDate(value, timeZone) {
  const d = coerceBillingDate(value);
  if (!d) return "Not scheduled yet";

  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    timeZoneName: "short",
  };

  if (timeZone) {
    options.timeZone = timeZone;
  }

  try {
    return d.toLocaleString(undefined, options);
  } catch (error) {
    const fallbackOptions = { ...options };
    delete fallbackOptions.timeZone;
    return d.toLocaleString(undefined, fallbackOptions);
  }
}

function formatBillingDateOnly(value, timeZone) {
  const d = coerceBillingDate(value);
  if (!d) return "Not scheduled yet";

  const options = {
    year: "numeric",
    month: "long",
    day: "numeric",
  };

  if (timeZone) {
    options.timeZone = timeZone;
  }

  try {
    return d.toLocaleDateString("en-GB", options);
  } catch (error) {
    const fallbackOptions = { ...options };
    delete fallbackOptions.timeZone;
    return d.toLocaleDateString("en-GB", fallbackOptions);
  }
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function addDaysToBillingDate(value, days) {
  const baseDate = coerceBillingDate(value);
  return baseDate ? addDays(baseDate, days) : null;
}

function getLatestPayerCurrentPeriodEnd(payerStripeAccounts) {
  if (
    !payerStripeAccounts ||
    typeof payerStripeAccounts !== "object" ||
    Array.isArray(payerStripeAccounts)
  ) {
    return null;
  }

  let latestDate = null;

  Object.values(payerStripeAccounts).forEach((record) => {
    const candidate = coerceBillingDate(record?.currentPeriodEnd);
    if (!candidate) return;

    if (!latestDate || candidate.getTime() > latestDate.getTime()) {
      latestDate = candidate;
    }
  });

  return latestDate;
}

function formatPromoBenefit(promo, fallbackCurrency = "CAD") {
  if (!promo) return "Configured discount";

  if (promo.discountType === "percent") {
    return `${promo.discountPercent || 0}% off`;
  }

  if (
    promo.discountType === "fixed_minor" &&
    typeof promo.fixedAmountMinor === "number"
  ) {
    return formatMoneyAmount(
      promo.fixedAmountMinor / 100,
      promo.fixedAmountCurrency || fallbackCurrency
    );
  }

  return "Configured discount";
}

function formatPromoDuration(promo) {
  if (!promo) return "Duration not specified";

  if (promo.durationMode === "forever") {
    return "No cycle limit";
  }

  if (typeof promo.remainingCycles === "number") {
    return `${promo.remainingCycles} billing cycle${
      promo.remainingCycles === 1 ? "" : "s"
    } remaining`;
  }

  return promo.durationMode || "Duration not specified";
}

function formatPromoDetails(promo, timeZone) {
  if (!promo) return "Duration not specified";

  const parts = [formatPromoDuration(promo)];

  if (promo.summary) {
    parts.push(promo.summary);
  }

  if (promo.activeUntil) {
    parts.push(`Active until ${formatBillingDate(promo.activeUntil, timeZone)}`);
  }

  return parts.join(" • ");
}

function amountToMinor(amount) {
  if (typeof amount !== "number" || !Number.isFinite(amount)) {
    return null;
  }

  return Math.round(amount * 100);
}

function minorToAmount(amountMinor) {
  if (typeof amountMinor !== "number" || !Number.isFinite(amountMinor)) {
    return null;
  }

  return amountMinor / 100;
}

function getPlanUnitPriceForCurrency(planKey, plan, currency, isLaunchBasicPromoActive) {
  if (!plan) return null;

  if (planKey === "basic" && isLaunchBasicPromoActive) {
    return 0;
  }

  const pricing = plan.pricing || {};

  switch (currency) {
    case "USD":
      return typeof pricing.usdPerUserMonthly === "number"
        ? pricing.usdPerUserMonthly
        : typeof pricing.cadPerUserMonthly === "number"
          ? pricing.cadPerUserMonthly
          : null;
    case "EUR":
      return typeof pricing.eurPerUserMonthly === "number"
        ? pricing.eurPerUserMonthly
        : typeof pricing.cadPerUserMonthly === "number"
          ? pricing.cadPerUserMonthly
          : null;
    case "GBP":
      return typeof pricing.gbpPerUserMonthly === "number"
        ? pricing.gbpPerUserMonthly
        : typeof pricing.cadPerUserMonthly === "number"
          ? pricing.cadPerUserMonthly
          : null;
    case "CAD":
    default:
      return typeof pricing.cadPerUserMonthly === "number"
        ? pricing.cadPerUserMonthly
        : null;
  }
}

function getStoragePriceForCurrency(storageAddOn, currency) {
  if (!storageAddOn?.pricing) return 0;

  switch (currency) {
    case "USD":
      return storageAddOn.pricing.usdMonthly ?? storageAddOn.pricing.cadMonthly ?? 0;
    case "EUR":
      return storageAddOn.pricing.eurMonthly ?? storageAddOn.pricing.cadMonthly ?? 0;
    case "GBP":
      return storageAddOn.pricing.gbpMonthly ?? storageAddOn.pricing.cadMonthly ?? 0;
    case "CAD":
    default:
      return storageAddOn.pricing.cadMonthly ?? 0;
  }
}

function getResolvedStorageAddOnKey(billingSummary, billing) {
  return (
    getNormalizedString(billingSummary?.selectedStorageAddOnKey) ||
    getNormalizedString(billing?.selectedStorageAddOnKey) ||
    null
  );
}

function getNormalizedString(value) {
  return typeof value === "string" && value.trim().length > 0 ? value.trim() : null;
}

function buildBillingCenterPath(teamId) {
  if (isDesktopShell) {
    const params = new URLSearchParams();
    params.set("tab", "billing");
    if (teamId) {
      params.set("teamId", teamId);
    }
    return `/settings?${params.toString()}`;
  }

  return buildTeamScopedPath("/billing", teamId);
}

function buildBillingDetailsPayload(addressValue) {
  if (!addressValue || typeof addressValue !== "object") {
    return null;
  }

  const address = addressValue.address || {};

  return {
    name: getNormalizedString(addressValue.name),
    phone: getNormalizedString(addressValue.phone),
    address: {
      line1: getNormalizedString(address.line1),
      line2: getNormalizedString(address.line2),
      city: getNormalizedString(address.city),
      state: getNormalizedString(address.state),
      postal_code: getNormalizedString(address.postal_code),
      country: getNormalizedString(address.country),
    },
  };
}

function createDesktopBillingAddressState() {
  return { ...DEFAULT_DESKTOP_BILLING_ADDRESS };
}

function buildDesktopBillingAddressValue(formState) {
  const nextState = formState && typeof formState === "object" ? formState : {};

  return {
    name: getNormalizedString(nextState.name),
    phone: null,
    address: {
      line1: getNormalizedString(nextState.line1),
      line2: getNormalizedString(nextState.line2),
      city: getNormalizedString(nextState.city),
      state: getNormalizedString(nextState.state),
      postal_code: getNormalizedString(nextState.postalCode),
      country: getNormalizedString(nextState.country),
    },
  };
}

function getDesktopBillingAddressError(addressValue) {
  const address = addressValue?.address || {};

  if (!addressValue?.name) {
    return "Please enter the billing name.";
  }

  if (!address.country) {
    return "Please choose the billing country or region.";
  }

  if (!address.line1) {
    return "Please enter the billing address line 1.";
  }

  if (!address.city) {
    return "Please enter the billing city.";
  }

  if (!address.state) {
    return "Please enter the billing province, state, or region.";
  }

  if (!address.postal_code) {
    return "Please enter the billing postal code.";
  }

  return "";
}

function DesktopBillingAddressFields({ value, onChange }) {
  const currentValue =
    value && typeof value === "object" ? value : DEFAULT_DESKTOP_BILLING_ADDRESS;

  const handleFieldChange = useCallback(
    (field) => (event) => {
      const nextValue = event.target.value;
      onChange((previousValue) => ({
        ...(previousValue && typeof previousValue === "object"
          ? previousValue
          : DEFAULT_DESKTOP_BILLING_ADDRESS),
        [field]: nextValue,
      }));
    },
    [onChange]
  );

  return (
    <div className="team-setup-payers__desktop-address-grid">
      <label className="team-setup-payers__desktop-address-field team-setup-payers__desktop-address-field--full">
        <span className="team-setup-payers__desktop-address-label">Full name</span>
        <input
          type="text"
          className="team-setup-payers__text-input"
          autoComplete="name"
          value={currentValue.name}
          onChange={handleFieldChange("name")}
        />
      </label>

      <label className="team-setup-payers__desktop-address-field team-setup-payers__desktop-address-field--full">
        <span className="team-setup-payers__desktop-address-label">Country or region</span>
        <select
          className="team-setup-payers__select team-setup-payers__desktop-address-select"
          autoComplete="country"
          value={currentValue.country}
          onChange={handleFieldChange("country")}
        >
          {BILLING_COUNTRY_OPTIONS.map((country) => (
            <option key={country.value} value={country.value}>
              {country.label}
            </option>
          ))}
        </select>
      </label>

      <label className="team-setup-payers__desktop-address-field team-setup-payers__desktop-address-field--full">
        <span className="team-setup-payers__desktop-address-label">Address line 1</span>
        <input
          type="text"
          className="team-setup-payers__text-input"
          autoComplete="address-line1"
          value={currentValue.line1}
          onChange={handleFieldChange("line1")}
        />
      </label>

      <label className="team-setup-payers__desktop-address-field team-setup-payers__desktop-address-field--full">
        <span className="team-setup-payers__desktop-address-label">Address line 2</span>
        <input
          type="text"
          className="team-setup-payers__text-input"
          autoComplete="address-line2"
          value={currentValue.line2}
          onChange={handleFieldChange("line2")}
        />
      </label>

      <label className="team-setup-payers__desktop-address-field">
        <span className="team-setup-payers__desktop-address-label">City</span>
        <input
          type="text"
          className="team-setup-payers__text-input"
          autoComplete="address-level2"
          value={currentValue.city}
          onChange={handleFieldChange("city")}
        />
      </label>

      <label className="team-setup-payers__desktop-address-field">
        <span className="team-setup-payers__desktop-address-label">Province / State</span>
        <input
          type="text"
          className="team-setup-payers__text-input"
          autoComplete="address-level1"
          value={currentValue.state}
          onChange={handleFieldChange("state")}
        />
      </label>

      <label className="team-setup-payers__desktop-address-field team-setup-payers__desktop-address-field--full">
        <span className="team-setup-payers__desktop-address-label">Postal code</span>
        <input
          type="text"
          className="team-setup-payers__text-input"
          autoComplete="postal-code"
          value={currentValue.postalCode}
          onChange={handleFieldChange("postalCode")}
        />
      </label>
    </div>
  );
}

function getSelectedPayersFromBilling(billing) {
  if (
    billing?.pendingNextCycleChange === true &&
    Array.isArray(billing?.nextCycleSelectedPayers)
  ) {
    return billing.nextCycleSelectedPayers.filter(
      (value) => typeof value === "string" && value.trim().length > 0
    );
  }

  if (Array.isArray(billing?.selectedPayers)) {
    return billing.selectedPayers.filter(
      (value) => typeof value === "string" && value.trim().length > 0
    );
  }

  return [];
}

function getPayerApprovalsFromBilling(billing) {
  if (
    billing?.pendingNextCycleChange === true &&
    billing?.nextCyclePayerApprovals &&
    typeof billing.nextCyclePayerApprovals === "object"
  ) {
    return billing.nextCyclePayerApprovals;
  }

  if (billing?.payerApprovals && typeof billing.payerApprovals === "object") {
    return billing.payerApprovals;
  }

  return {};
}

function getPaymentMethodsReadyFromBilling(billing) {
  if (
    billing?.pendingNextCycleChange === true &&
    billing?.nextCyclePaymentMethodsReady &&
    typeof billing.nextCyclePaymentMethodsReady === "object"
  ) {
    return billing.nextCyclePaymentMethodsReady;
  }

  if (
    billing?.paymentMethodsReady &&
    typeof billing.paymentMethodsReady === "object"
  ) {
    return billing.paymentMethodsReady;
  }

  return {};
}

function getPayerStripeAccountsFromBilling(billing) {
  if (
    billing?.payerStripeAccounts &&
    typeof billing.payerStripeAccounts === "object" &&
    !Array.isArray(billing.payerStripeAccounts)
  ) {
    return billing.payerStripeAccounts;
  }

  return {};
}

function getPaymentSplitModeFromBilling(billing) {
  if (
    billing?.pendingNextCycleChange === true &&
    typeof billing?.nextCyclePaymentSplitMode === "string" &&
    billing.nextCyclePaymentSplitMode.trim().length > 0
  ) {
    return billing.nextCyclePaymentSplitMode.trim();
  }

  if (
    typeof billing?.paymentSplitMode === "string" &&
    billing.paymentSplitMode.trim().length > 0
  ) {
    return billing.paymentSplitMode.trim();
  }

  return "";
}

function getPayerDecisionStatus(approvalEntry) {
  const approvalExpiresAt = coerceBillingDate(approvalEntry?.approvalExpiresAt);

  if (
    approvalEntry?.approved === true &&
    approvalExpiresAt instanceof Date &&
    Date.now() >= approvalExpiresAt.getTime()
  ) {
    return "expired";
  }

  if (approvalEntry?.approved === true) {
    return "approved";
  }

  if (approvalEntry?.revokedAt || approvalEntry?.revokedBy) {
    return "revoked";
  }

  if (approvalEntry?.removedAt || approvalEntry?.removedBy) {
    return "removed";
  }

  return "pending";
}

function buildDisplayNameList(ids, profilesByUid = {}, currentUid = null) {
  return (Array.isArray(ids) ? ids : [])
    .filter((uid) => typeof uid === "string" && uid.trim().length > 0)
    .map((uid) => {
      const profile = profilesByUid?.[uid] || {};
      const baseName = profile?.displayName || uid;
      return uid === currentUid ? `${baseName} (You)` : baseName;
    });
}

function TrialVerificationCard({
  currentUid,
  teamMembers,
  memberProfiles,
  billingBreakdown,
  teamBillingTimeZone,
  onRefresh,
}) {
  const [phoneCountryCode, setPhoneCountryCode] = useState("+1");
  const [phoneLocalNumber, setPhoneLocalNumber] = useState("");
  const [verificationId, setVerificationId] = useState("");
  const [smsCode, setSmsCode] = useState("");
  const [sendingCode, setSendingCode] = useState(false);
  const [verifyingCode, setVerifyingCode] = useState(false);
  const [trialError, setTrialError] = useState("");
  const [trialSuccess, setTrialSuccess] = useState("");

  const recaptchaVerifierRef = useRef(null);
  const recaptchaContainerId = `team-setup-payers-phone-recaptcha-${currentUid || "anon"}`;

  const currentUserProfile = memberProfiles?.[currentUid] || {};
  const currentUserPhoneVerified = currentUserProfile?.phoneVerified === true;
  const currentUserPhoneVerificationNeedsRepair =
    currentUserProfile?.phoneVerificationNeedsRepair === true;
  const trialConsumed = billingBreakdown?.trialConsumed === true;

  const missingPhoneVerificationMembers = useMemo(() => {
    return (teamMembers || []).filter(
      ({ profile }) => profile?.phoneVerified !== true
    );
  }, [teamMembers]);

  const trialActive = billingBreakdown?.trialActive === true;
  const trialEligibleByPolicy = billingBreakdown?.trialEligibleByPolicy === true;
  const trialReadyToStart = billingBreakdown?.trialReadyToStart === true;
  const trialConsumedEndedAt = billingBreakdown?.trialConsumedEndedAt || null;
  const shouldCollapseConsumedTrial = trialConsumed && !trialEligibleByPolicy;
  const [showTrialDetails, setShowTrialDetails] = useState(
    !shouldCollapseConsumedTrial
  );

  useEffect(() => {
    setShowTrialDetails(!shouldCollapseConsumedTrial);
  }, [shouldCollapseConsumedTrial]);

  const trialSummaryTitle = trialActive
    ? "Free Trial Active"
    : shouldCollapseConsumedTrial
      ? "Free Trial Terminated"
      : trialEligibleByPolicy
        ? `Starts with ${FREE_TRIAL_DAYS}-Day Free Trial`
        : "Not Eligible for Free Trial";

  const trialSummaryCopy = shouldCollapseConsumedTrial
    ? trialConsumedEndedAt
      ? `The team free trial ended ${formatBillingDateOnly(trialConsumedEndedAt, teamBillingTimeZone)}. Restarting or renewing access begins a paid cycle.`
      : "This team has already used its free trial. Restarting or renewing access begins a paid cycle."
    : "All team members must complete phone verification for the team to get the free trial.";

  const trialStatusToneClass = trialActive
    ? "team-setup-payers__trial-status-value--eligible"
    : trialEligibleByPolicy && trialReadyToStart
      ? "team-setup-payers__trial-status-value--eligible"
      : trialEligibleByPolicy && !trialReadyToStart
        ? "team-setup-payers__trial-status-value--waiting"
        : "team-setup-payers__trial-status-value--ineligible";

  const trialStatusValue = trialActive
    ? "Free trial active"
    : shouldCollapseConsumedTrial
      ? trialConsumedEndedAt
        ? `Ended ${formatBillingDateOnly(trialConsumedEndedAt, teamBillingTimeZone)}`
        : "Free trial already used"
      : trialEligibleByPolicy
        ? trialReadyToStart
          ? `Completed - starts with ${FREE_TRIAL_DAYS}-day free trial`
          : "Eligible - waiting for all team members to complete phone verification"
        : "Not Eligible";

  useEffect(() => {
    return () => {
      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (_) {}
        recaptchaVerifierRef.current = null;
      }
    };
  }, []);

  const getOrCreateRecaptchaVerifier = () => {
    if (recaptchaVerifierRef.current) {
      return recaptchaVerifierRef.current;
    }

    const isLocalhost =
      window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1";

    if (isLocalhost) {
      auth.settings.appVerificationDisabledForTesting = true;
    }

    const verifier = new RecaptchaVerifier(auth, recaptchaContainerId, {
      size: "invisible",
    });

    recaptchaVerifierRef.current = verifier;
    return verifier;
  };


  const handleSendVerificationCode = async () => {
    setTrialError("");
    setTrialSuccess("");

    const digitsOnlyLocal = phoneLocalNumber.replace(/[^\d]/g, "");
    const normalizedPhone = `${phoneCountryCode}${digitsOnlyLocal}`;

    if (!digitsOnlyLocal) {
      setTrialError("Please enter your phone number.");
      return;
    }

    if (!phoneCountryCode.startsWith("+")) {
      setTrialError("Please choose a valid country code.");
      return;
    }

    if (!auth.currentUser) {
      setTrialError("You must be signed in.");
      return;
    }

    try {
      setSendingCode(true);

      const checkPhoneVerificationEligibility = httpsCallable(
        functions,
        FUNCTION_NAMES.checkPhoneVerificationEligibility
      );

      await checkPhoneVerificationEligibility({
        phoneNumber: normalizedPhone,
      });

      const provider = new PhoneAuthProvider(auth);
      const verifier = getOrCreateRecaptchaVerifier();

      const nextVerificationId = await provider.verifyPhoneNumber(
        normalizedPhone,
        verifier
      );

      setVerificationId(nextVerificationId);
      setTrialSuccess("Verification code sent. Please check your phone and enter the code below.");
    } catch (error) {
      console.error("handleSendVerificationCode error:", error);
      setTrialError(
        error?.message ||
          "Failed to send verification code. Please try again."
      );

      if (recaptchaVerifierRef.current) {
        try {
          recaptchaVerifierRef.current.clear();
        } catch (_) {}
        recaptchaVerifierRef.current = null;
      }
    } finally {
      setSendingCode(false);
    }
  };

  const handleVerifyCode = async () => {
    setTrialError("");
    setTrialSuccess("");

    if (!auth.currentUser) {
      setTrialError("You must be signed in.");
      return;
    }

    if (!verificationId) {
      setTrialError("Please request a verification code first.");
      return;
    }

    if (!smsCode.trim()) {
      setTrialError("Please enter the SMS verification code.");
      return;
    }

    try {
      setVerifyingCode(true);

      const credential = PhoneAuthProvider.credential(
        verificationId,
        smsCode.trim()
      );

      try {
        await linkWithCredential(auth.currentUser, credential);
      } catch (linkError) {
        if (linkError?.code !== "auth/provider-already-linked") {
          throw linkError;
        }
      }

      await auth.currentUser.getIdToken(true);

      const saveMyVerifiedPhone = httpsCallable(
        functions,
        FUNCTION_NAMES.saveMyVerifiedPhone
      );

      try {
        await saveMyVerifiedPhone({});
      } catch (saveError) {
        if (saveError?.code === "functions/failed-precondition") {
          try {
            await unlink(auth.currentUser, "phone");
            await auth.currentUser.getIdToken(true);
          } catch (unlinkError) {
            console.warn("Failed to unlink phone after verification rejection:", unlinkError);
          }
        }
        throw saveError;
      }

      setTrialSuccess("Your phone verification is complete.");
      setSmsCode("");
      setVerificationId("");

      await onRefresh();
    } catch (error) {
      console.error("handleVerifyCode error:", error);

      if (error?.code === "auth/credential-already-in-use") {
        setTrialError(
          "This phone number is already linked to another account and cannot be used to qualify for a new free trial."
        );
      } else if (error?.code === "functions/failed-precondition") {
        setTrialError(
          error?.message ||
            "This phone number cannot be used for verification right now."
        );
      } else {
        setTrialError(
          error?.message ||
            "Failed to verify your phone number. Please try again."
        );
      }
    } finally {
      setVerifyingCode(false);
    }
  };

  return (
    <div className="team-setup-payers__summary-card">
      <div className="team-setup-payers__summary-header">
        <div className="team-setup-payers__summary-header-copy">
          <div className="team-setup-payers__summary-kicker">Trial</div>
          <div className="team-setup-payers__summary-plan">{trialSummaryTitle}</div>
          <p className="team-setup-payers__summary-copy">{trialSummaryCopy}</p>
        </div>

        {shouldCollapseConsumedTrial ? (
          <button
            type="button"
            className="team-setup-payers__collapse-button"
            aria-expanded={showTrialDetails}
            onClick={() => setShowTrialDetails((current) => !current)}
          >
            {showTrialDetails ? "Hide details" : "Show details"}
          </button>
        ) : null}
      </div>

      <div className="team-setup-payers__trial-status-box">
        <div className="team-setup-payers__trial-status-row">
          <span className="team-setup-payers__trial-status-label">Team free-trial status</span>
          <span className={`team-setup-payers__trial-status-value ${trialStatusToneClass}`}>
            {trialStatusValue}
          </span>
        </div>

        {!trialConsumed && missingPhoneVerificationMembers.length > 0 ? (
          <div className="team-setup-payers__trial-status-row">
            <span className="team-setup-payers__trial-status-label team-setup-payers__trial-status-value--ineligible">Still missing verification</span>
            <span className="team-setup-payers__trial-status-value team-setup-payers__trial-status-value--ineligible">
              {missingPhoneVerificationMembers.length}
            </span>
          </div>
        ) : null}
      </div>

      {showTrialDetails ? (
        <>
          {trialConsumed ? (
            <div className="team-setup-payers__note-box">
              {trialConsumedEndedAt
                ? `Phone verification no longer affects this team's free-trial eligibility because the free trial ended on ${formatBillingDateOnly(trialConsumedEndedAt, teamBillingTimeZone)}.`
                : "Phone verification no longer affects this team's free-trial eligibility because the free trial has already been used."}
            </div>
          ) : missingPhoneVerificationMembers.length > 0 ? (
            <div className="team-setup-payers__trial-member-list">
              {missingPhoneVerificationMembers.map(({ uid, profile }) => (
                <div key={uid} className="team-setup-payers__trial-member-item">
                  {(profile?.displayName || uid) +
                    (uid === currentUid ? " (You)" : "") +
                    (profile?.phoneVerificationNeedsRepair
                      ? " - verification needs to be refreshed"
                      : "")}
                </div>
              ))}
            </div>
          ) : (
            <div className="team-setup-payers__note-box">
              All current team members have completed phone verification.
            </div>
          )}
        </>
      ) : (
        <div className="team-setup-payers__summary-note">
          Expand this card to review why the team no longer qualifies for another free trial.
        </div>
      )}

      {!showTrialDetails || trialConsumed ? null : currentUserPhoneVerified ? (
        <div className="team-setup-payers__note-box">
          Your phone verification is already complete.
        </div>
      ) : (
        <div className="team-setup-payers__summary-row team-setup-payers__summary-row--stack">
          <label className="team-setup-payers__field-label">
            {currentUserPhoneVerificationNeedsRepair
              ? "Refresh your phone verification for free-trial eligibility"
              : "Verify your phone for free-trial eligibility"}
          </label>

          {currentUserPhoneVerificationNeedsRepair ? (
            <div className="team-setup-payers__note-box">
              Your previous phone verification is missing required trial-safety
              data. Re-complete phone verification to repair the record.
            </div>
          ) : null}

          <div className="team-setup-payers__phone-input-row">
            <select
              className="team-setup-payers__select team-setup-payers__phone-code-select"
              value={phoneCountryCode}
              onChange={(e) => setPhoneCountryCode(e.target.value)}
            >
              <option value="+1">Canada / USA (+1)</option>
              <option value="+44">United Kingdom (+44)</option>
              <option value="+33">France (+33)</option>
              <option value="+49">Germany (+49)</option>
              <option value="+34">Spain (+34)</option>
              <option value="+39">Italy (+39)</option>
              <option value="+31">Netherlands (+31)</option>
              <option value="+32">Belgium (+32)</option>
              <option value="+41">Switzerland (+41)</option>
              <option value="+46">Sweden (+46)</option>
              <option value="+47">Norway (+47)</option>
              <option value="+45">Denmark (+45)</option>
              <option value="+61">Australia (+61)</option>
              <option value="+64">New Zealand (+64)</option>
              <option value="+81">Japan (+81)</option>
              <option value="+82">South Korea (+82)</option>
            </select>

            <input
              type="tel"
              className="team-setup-payers__text-input team-setup-payers__phone-number-input"
              placeholder="5145550123"
              value={phoneLocalNumber}
              onChange={(e) => setPhoneLocalNumber(e.target.value)}
            />
          </div>

          <button
            type="button"
            className="team-setup-payers__button team-setup-payers__button--muted"
            onClick={handleSendVerificationCode}
            disabled={sendingCode}
          >
            {sendingCode ? "Sending code..." : "Send Verification Code"}
          </button>

          {verificationId ? (
            <>
              <input
                type="text"
                className="team-setup-payers__text-input"
                placeholder="Enter SMS code"
                value={smsCode}
                onChange={(e) => setSmsCode(e.target.value)}
              />

              <button
                type="button"
                className="team-setup-payers__button"
                onClick={handleVerifyCode}
                disabled={verifyingCode}
              >
                {verifyingCode ? "Verifying..." : "Complete Phone Verification"}
              </button>
            </>
          ) : null}

          {trialError ? (
            <div className="team-setup-payers__error">{trialError}</div>
          ) : null}

          {trialSuccess ? (
            <div className="team-setup-payers__summary-note">{trialSuccess}</div>
          ) : null}

          <div className="team-setup-payers__summary-note">
            This phone verification is used only to determine free-trial eligibility and help prevent repeated trial abuse across newly created accounts.
          </div>

          <div className="team-setup-payers__billing-legal-box">
            <p className="team-setup-payers__billing-legal-text">
              Phone verification is used to assess free-trial eligibility, help prevent repeated trial abuse, and connect verified phone status to your account.
            </p>

            <p className="team-setup-payers__billing-legal-text">
              This process is governed by our{" "}
              <a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="team-setup-payers__billing-legal-link"
              >
                Privacy Policy
              </a>{" "}
              and{" "}
              <a
                href="/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="team-setup-payers__billing-legal-link"
              >
                Terms of Service
              </a>.
            </p>
          </div>

          <div
            id={recaptchaContainerId}
            className="team-setup-payers__phone-recaptcha"
          />
        </div>
      )}
    </div>
  );
}

function PendingJoinProrateCard({
  teamId,
  pendingProrateSummary,
  onRefresh,
  teamBillingTimeZone,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [submitting, setSubmitting] = useState(false);
  const [cardError, setCardError] = useState("");
  const [cardSuccess, setCardSuccess] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [desktopBillingAddress, setDesktopBillingAddress] = useState(
    createDesktopBillingAddressState
  );

  const handleConfirmProrate = async (e) => {
    e.preventDefault();
    setCardError("");
    setCardSuccess("");

    if (!teamId) {
      setCardError("Missing team.");
      return;
    }

    if (!pendingProrateSummary) {
      setCardError("No pending prorate charge is available.");
      return;
    }

    if (!acceptedTerms) {
      setCardError(
        "Please acknowledge the one-time prorate notice, Privacy Policy, and Terms before continuing."
      );
      return;
    }

    try {
      setSubmitting(true);

      if (!stripe || !elements) {
        setCardError("Stripe has not loaded yet.");
        return;
      }

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) {
        setCardError("Card form is not ready.");
        return;
      }
      let addressValue = null;

      if (isDesktopShell) {
        addressValue = buildDesktopBillingAddressValue(desktopBillingAddress);
        const addressError = getDesktopBillingAddressError(addressValue);

        if (addressError) {
          setCardError(addressError);
          return;
        }
      } else {
        const addressElement = elements.getElement("address");

        if (!addressElement) {
          setCardError("Address form is not ready.");
          return;
        }

        const {
          error: addressError,
          complete: addressComplete,
          value: nextAddressValue,
        } = await addressElement.getValue();

        if (addressError) {
          setCardError(addressError.message || "Please complete your billing address.");
          return;
        }

        if (!addressComplete) {
          setCardError("Please complete your billing address.");
          return;
        }

        addressValue = nextAddressValue;
      }

      const billingDetails = buildBillingDetailsPayload(addressValue);

      const beginProrate = httpsCallable(
        functions,
        FUNCTION_NAMES.createPendingJoinProratePayment
      );

      const beginResult = await beginProrate({
        teamId,
        billingDetails,
      });
      const beginData = beginResult?.data || {};

      if (beginData?.promoted === true) {
        setCardSuccess("Your pending team join is now complete.");
        await onRefresh();
        window.dispatchEvent(new Event("payer-action-overlay:refresh"));
        return;
      }

      let paymentIntentId = getNormalizedString(beginData?.paymentIntentId);

      if (beginData?.paymentIntentStatus === "succeeded" && paymentIntentId) {
        const finalizeProrate = httpsCallable(
          functions,
          FUNCTION_NAMES.finalizePendingJoinProratePayment
        );
        await finalizeProrate({
          teamId,
          paymentIntentId,
        });
        setCardSuccess("Your prorated join payment was confirmed and your team access is now active.");
        await onRefresh();
        window.dispatchEvent(new Event("payer-action-overlay:refresh"));
        return;
      }

      if (!beginData?.clientSecret) {
        throw new Error("Did not receive a payment client secret.");
      }

      const confirmed = await stripe.confirmCardPayment(beginData.clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            address: {
              line1: addressValue.address.line1 || "",
              line2: addressValue.address.line2 || "",
              city: addressValue.address.city || "",
              state: addressValue.address.state || "",
              postal_code: addressValue.address.postal_code || "",
              country: addressValue.address.country || "",
            },
            name: addressValue.name || undefined,
            phone: addressValue.phone || undefined,
          },
        },
      });

      if (confirmed.error) {
        throw new Error(confirmed.error.message || "Prorated join payment failed.");
      }

      paymentIntentId =
        getNormalizedString(confirmed?.paymentIntent?.id) || paymentIntentId;

      if (!paymentIntentId) {
        throw new Error("No payment intent was returned from Stripe.");
      }

      const finalizeProrate = httpsCallable(
        functions,
        FUNCTION_NAMES.finalizePendingJoinProratePayment
      );

      await finalizeProrate({
        teamId,
        paymentIntentId,
      });

      setCardSuccess("Your one-time prorate payment was completed and your team access is now active.");
      await onRefresh();
      window.dispatchEvent(new Event("payer-action-overlay:refresh"));
    } catch (error) {
      console.error("Pending join prorate payment error:", error);
      setCardError(
        error?.message ||
          "Failed to complete the one-time prorated join payment."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!pendingProrateSummary || pendingProrateSummary.status !== "pending_prorate_payment") {
    return null;
  }

  if (pendingProrateSummary.eligible !== true) {
    return (
      <div className="team-setup-payers__note-box">
        This pending join cannot currently be completed because the active billing cycle
        changed or ended. Ask the team owner to review the team billing state before
        trying again.
      </div>
    );
  }

  return (
    <form
      onSubmit={handleConfirmProrate}
      className="team-setup-payers__summary-row team-setup-payers__summary-row--stack"
    >
      <div className="team-setup-payers__note-box">
        <strong>
          {pendingProrateSummary.chargeRequired
            ? "One-time prorated join payment"
            : "Pending join activation"}
        </strong>
        <div>
          Current plan seat price:{" "}
          {formatMoneyAmount(
            (pendingProrateSummary.monthlySeatAmountMinor || 0) / 100,
            pendingProrateSummary.billingCurrency || "CAD"
          )}{" "}
          / month
        </div>
        <div>
          Time remaining in this billing cycle:{" "}
          {pendingProrateSummary.remainingDaysCeil} day
          {pendingProrateSummary.remainingDaysCeil === 1 ? "" : "s"}
        </div>
        <div>
          Current cycle ends:{" "}
          {formatBillingDate(
            pendingProrateSummary.currentPeriodEnd,
            teamBillingTimeZone
          )}
        </div>
        <div>
          Subtotal before tax:{" "}
          <strong>
            {formatMoneyAmount(
              ((typeof pendingProrateSummary.subtotalMinor === "number"
                ? pendingProrateSummary.subtotalMinor
                : pendingProrateSummary.prorateAmountMinor) || 0) / 100,
              pendingProrateSummary.billingCurrency || "CAD"
            )}
          </strong>
        </div>
        <div>
          Estimated tax:{" "}
          <strong>
            {formatMoneyAmount(
              (pendingProrateSummary.taxMinor || 0) / 100,
              pendingProrateSummary.billingCurrency || "CAD"
            )}
          </strong>
        </div>
        <div>
          Total due now:{" "}
          <strong>
            {formatMoneyAmount(
              ((typeof pendingProrateSummary.totalMinor === "number"
                ? pendingProrateSummary.totalMinor
                : pendingProrateSummary.prorateAmountMinor) || 0) / 100,
              pendingProrateSummary.billingCurrency || "CAD"
            )}
          </strong>
        </div>
      </div>

      {pendingProrateSummary.isSelectedAsNextCyclePayer ? (
        <div className="team-setup-payers__note-box">
          You are also selected as a next-cycle payer. This one-time prorate charge only
          activates your current join. If the next billing cycle still needs payer approval
          or payment setup, you may still need to complete those next-cycle billing steps too.
        </div>
      ) : (
        <div className="team-setup-payers__note-box">
          This is a one-time mid-cycle charge only. It does not make you a recurring payer for
          future billing cycles by itself.
        </div>
      )}

      <div className="team-setup-payers__note-box">
        Taxes are added on top of the listed subscription prices based on the payer billing
        address. Your current catalog prices do not include tax.
      </div>

      {pendingProrateSummary.chargeRequired ? (
        <>
          <label className="team-setup-payers__field-label">
            Billing address
          </label>

          <div className="team-setup-payers__stripe-box">
            {isDesktopShell ? (
              <DesktopBillingAddressFields
                value={desktopBillingAddress}
                onChange={setDesktopBillingAddress}
              />
            ) : (
              <AddressElement
                options={{
                  mode: "billing",
                  fields: {
                    phone: "never",
                  },
                  defaultValues: {
                    address: {
                      country: "CA",
                    },
                  },
                }}
              />
            )}
          </div>

          <label className="team-setup-payers__field-label">
            Add your card
          </label>

          <div className="team-setup-payers__stripe-box">
            <CardElement
              options={{
                hidePostalCode: true,
                style: {
                  base: {
                    color: isDarkMode ? "#F4F8FB" : "#16313C",
                    fontSize: "1rem",
                    "::placeholder": {
                      color: isDarkMode ? "#99A9B4" : "#6F8290",
                    },
                    iconColor: isDarkMode ? "#F4F8FB" : "#16313C",
                  },
                  invalid: {
                    color: "#dd6262",
                    iconColor: "#dd6262",
                  },
                },
              }}
            />
          </div>
        </>
      ) : null}

      <label className="team-setup-payers__checkbox-row">
        <input
          type="checkbox"
          checked={acceptedTerms}
          onChange={(e) => setAcceptedTerms(e.target.checked)}
        />
        <span>
          I confirm this one-time prorated join charge is correct, I want to complete this
          pending team join now, and I accept the{" "}
          <a href="/terms-of-service" target="_blank" rel="noreferrer">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href="/privacy-policy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>.
        </span>
      </label>

      {cardError ? (
        <div className="team-setup-payers__error">{cardError}</div>
      ) : null}

      {cardSuccess ? (
        <div className="team-setup-payers__success">{cardSuccess}</div>
      ) : null}

      <button
        type="submit"
        className="team-setup-payers__button"
        disabled={submitting}
      >
        {submitting
          ? "Processing..."
          : pendingProrateSummary.chargeRequired
            ? "Pay One-Time Prorate And Join Team"
            : "Complete Pending Team Join"}
      </button>
    </form>
  );
}

function PendingJoinProrateNoChargeCard({
  teamId,
  pendingProrateSummary,
  onRefresh,
  teamBillingTimeZone,
}) {
  const [submitting, setSubmitting] = useState(false);
  const [cardError, setCardError] = useState("");
  const [cardSuccess, setCardSuccess] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleCompletePendingJoin = async (e) => {
    e.preventDefault();
    setCardError("");
    setCardSuccess("");

    if (!teamId) {
      setCardError("Missing team.");
      return;
    }

    if (!acceptedTerms) {
      setCardError(
        "Please acknowledge the pending join notice, Privacy Policy, and Terms before continuing."
      );
      return;
    }

    try {
      setSubmitting(true);

      const beginProrate = httpsCallable(
        functions,
        FUNCTION_NAMES.createPendingJoinProratePayment
      );

      const beginResult = await beginProrate({ teamId });
      const beginData = beginResult?.data || {};

      if (beginData?.promoted !== true) {
        throw new Error("The pending join was not completed.");
      }

      setCardSuccess("Your pending team join is now complete.");
      await onRefresh();
      window.dispatchEvent(new Event("payer-action-overlay:refresh"));
    } catch (error) {
      console.error("Pending join completion error:", error);
      setCardError(
        error?.message || "Failed to complete the pending team join."
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (!pendingProrateSummary || pendingProrateSummary.status !== "pending_prorate_payment") {
    return null;
  }

  if (pendingProrateSummary.eligible !== true) {
    return (
      <div className="team-setup-payers__note-box">
        This pending join cannot currently be completed because the active billing cycle
        changed or ended. Ask the team owner to review the team billing state before
        trying again.
      </div>
    );
  }

  return (
    <form
      onSubmit={handleCompletePendingJoin}
      className="team-setup-payers__summary-row team-setup-payers__summary-row--stack"
    >
      <div className="team-setup-payers__note-box">
        <strong>Complete pending team join</strong>
        <div>No card charge is required for this join.</div>
        <div>
          Current cycle ends:{" "}
          {formatBillingDate(
            pendingProrateSummary.currentPeriodEnd,
            teamBillingTimeZone
          )}
        </div>
      </div>

      <label className="team-setup-payers__checkbox-row">
        <input
          type="checkbox"
          checked={acceptedTerms}
          onChange={(e) => setAcceptedTerms(e.target.checked)}
        />
        <span>
          I confirm I want to complete this pending team join now and I accept the{" "}
          <a href="/terms-of-service" target="_blank" rel="noreferrer">
            Terms of Service
          </a>{" "}
          and{" "}
          <a href="/privacy-policy" target="_blank" rel="noreferrer">
            Privacy Policy
          </a>.
        </span>
      </label>

      {cardError ? (
        <div className="team-setup-payers__error">{cardError}</div>
      ) : null}

      {cardSuccess ? (
        <div className="team-setup-payers__success">{cardSuccess}</div>
      ) : null}

      <button
        type="submit"
        className="team-setup-payers__button"
        disabled={submitting}
      >
        {submitting ? "Processing..." : "Complete Pending Team Join"}
      </button>
    </form>
  );
}

function PayerCardSetupForm({
  teamId,
  billing,
  onRefresh,
  currentUid,
}) {
  const stripe = useStripe();
  const elements = useElements();

  const [submitting, setSubmitting] = useState(false);
  const [cardError, setCardError] = useState("");
  const [cardSuccess, setCardSuccess] = useState("");
  const [acceptedBillingTerms, setAcceptedBillingTerms] = useState(false);
  const [desktopBillingAddress, setDesktopBillingAddress] = useState(
    createDesktopBillingAddressState
  );

  const selectedPayers = useMemo(
    () => getSelectedPayersFromBilling(billing),
    [billing]
  );

  const isSelectedPayer = selectedPayers.includes(currentUid);

  const paymentMethodsReady = useMemo(
    () => getPaymentMethodsReadyFromBilling(billing),
    [billing]
  );

  const payerReady = paymentMethodsReady?.[currentUid]?.ready === true;

  const handleConfirmCard = async (e) => {
    e.preventDefault();
    setCardError("");
    setCardSuccess("");

    if (!teamId) {
      setCardError("Missing team.");
      return;
    }

    if (!isSelectedPayer) {
      setCardError("You are not currently one of the selected payers.");
      return;
    }

    if (!acceptedBillingTerms) {
      setCardError(
        "Please acknowledge the billing notice, Privacy Policy, and Terms before continuing."
      );
      return;
    }

    if (!stripe || !elements) {
      setCardError("Stripe has not loaded yet.");
      return;
    }

    const cardElement = elements.getElement(CardElement);

    if (!cardElement) {
      setCardError("Card form is not ready.");
      return;
    }
    let addressValue = null;

    if (isDesktopShell) {
      addressValue = buildDesktopBillingAddressValue(desktopBillingAddress);
      const addressError = getDesktopBillingAddressError(addressValue);

      if (addressError) {
        setCardError(addressError);
        return;
      }
    } else {
      const addressElement = elements.getElement("address");

      if (!addressElement) {
        setCardError("Address form is not ready.");
        return;
      }

      const {
        error: addressError,
        complete: addressComplete,
        value: nextAddressValue,
      } = await addressElement.getValue();

      if (addressError) {
        setCardError(addressError.message || "Please complete your billing address.");
        return;
      }

      if (!addressComplete) {
        setCardError("Please complete your billing address.");
        return;
      }

      addressValue = nextAddressValue;
    }

    try {
      setSubmitting(true);

      const beginSetup = httpsCallable(
        functions,
        FUNCTION_NAMES.markMyPayerPaymentMethodReady
      );

      const billingDetails = buildBillingDetailsPayload(addressValue);
      const beginResult = await beginSetup({ teamId, billingDetails });
      const beginData = beginResult?.data || {};

      if (beginData?.requiresClientConfirmation !== true) {
        if (beginData?.ready === true || beginData?.paymentMethodId) {
          setCardSuccess(
            beginData?.reusedExistingBillingAccount
              ? "Existing payment method reused successfully."
              : "Payment method saved successfully."
          );
          await onRefresh();
          window.dispatchEvent(new Event("payer-action-overlay:refresh"));
          return;
        }

        throw new Error("Did not receive a SetupIntent client secret.");
      }

      if (!beginData?.setupIntentClientSecret) {
        throw new Error("Did not receive a SetupIntent client secret.");
      }

      const confirmed = await stripe.confirmCardSetup(
        beginData.setupIntentClientSecret,
        {
          payment_method: {
            card: cardElement,
            billing_details: {
              address: {
                line1: addressValue.address.line1 || "",
                line2: addressValue.address.line2 || "",
                city: addressValue.address.city || "",
                state: addressValue.address.state || "",
                postal_code: addressValue.address.postal_code || "",
                country: addressValue.address.country || "",
              },
              name: addressValue.name || undefined,
              phone: addressValue.phone || undefined,
            },
          },
        }
      );

      if (confirmed.error) {
        throw new Error(confirmed.error.message || "Card setup failed.");
      }

      const paymentMethodId = getNormalizedString(
        confirmed?.setupIntent?.payment_method
      );

      if (!paymentMethodId) {
        throw new Error("No payment method was returned from Stripe.");
      }

      const finishSetup = httpsCallable(
        functions,
        FUNCTION_NAMES.markMyPayerPaymentMethodReady
      );

      await finishSetup({
        teamId,
        paymentMethodId,
        setupIntentId: beginData.setupIntentId || confirmed?.setupIntent?.id || null,
        billingDetails,
      });

      setCardSuccess("Payment method saved successfully.");
      await onRefresh();
      window.dispatchEvent(new Event("payer-action-overlay:refresh"));
    } catch (error) {
      console.error("Payer card setup error:", error);
      setCardError(error?.message || "Failed to save your payment method.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isSelectedPayer) {
    return (
      <div className="team-setup-payers__summary-note">
        You are not one of the selected payers for this billing cycle.
      </div>
    );
  }

  if (payerReady) {
    return (
      <div className="team-setup-payers__storage-addon-box">
        <div className="team-setup-payers__storage-addon-title">
          Payment method ready
        </div>
        <div className="team-setup-payers__storage-addon-copy">
          Your billing method is already connected for this team.
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleConfirmCard} className="team-setup-payers__summary-row team-setup-payers__summary-row--stack">
      <label className="team-setup-payers__field-label">
        Billing address
      </label>

      <div className="team-setup-payers__stripe-box">
        {isDesktopShell ? (
          <DesktopBillingAddressFields
            value={desktopBillingAddress}
            onChange={setDesktopBillingAddress}
          />
        ) : (
          <AddressElement
            options={{
              mode: "billing",
              fields: {
                phone: "never",
              },
              defaultValues: {
                address: {
                  country: "CA",
                },
              },
            }}
          />
        )}
      </div>

      <label className="team-setup-payers__field-label">
        Add your card
      </label>

      <div className="team-setup-payers__stripe-box">
        <CardElement
          options={{
            hidePostalCode: true,
            style: {
              base: {
                color: "#F4F8FB",
                fontSize: "1rem",
                "::placeholder": {
                  color: isDarkMode ? "#99A9B4" : "#6F8290",
                },
                iconColor: "#F4F8FB",
              },
              invalid: {
                color: "#C93535",
                iconColor: "#C93535",
              },
            },
          }}
        />
      </div>

      {cardError ? (
        <div className="team-setup-payers__error">{cardError}</div>
      ) : null}

      {cardSuccess ? (
        <div className="team-setup-payers__summary-note">{cardSuccess}</div>
      ) : null}

      <div className="team-setup-payers__billing-legal-box">
        <p className="team-setup-payers__billing-legal-text">
          Billing name, billing address, and payment method details are processed
          by Stripe for payment setup and subscription billing. We store only the
          identifiers and status data needed to manage your subscription.
        </p>

        <p className="team-setup-payers__billing-legal-text">
          By continuing, you acknowledge that subscription billing is governed by
          our{" "}
          <a
            href="/privacy-policy"
            target="_blank"
            rel="noopener noreferrer"
            className="team-setup-payers__billing-legal-link"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="/terms-of-service"
            target="_blank"
            rel="noopener noreferrer"
            className="team-setup-payers__billing-legal-link"
          >
            Terms of Service
          </a>.
        </p>

        <label className="team-setup-payers__billing-ack">
          <input
            type="checkbox"
            checked={acceptedBillingTerms}
            onChange={(e) => setAcceptedBillingTerms(e.target.checked)}
          />
          <span>
            I acknowledge the billing notice and have reviewed the Privacy Policy
            and Terms of Service.
          </span>
        </label>
      </div>

      <button
        type="submit"
        className="team-setup-payers__button"
        disabled={submitting || !stripe || !elements || !acceptedBillingTerms}
      >
        {submitting ? "Saving Card..." : "Save Payment Method"}
      </button>
    </form>
  );
}

const TeamSetupPayers = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { teamId: contextTeamId } = useTeam();
  const { refreshAccess } = useAccess() || {};
  const requestedTeamId = getRequestedTeamId(location.search);
  const teamId = requestedTeamId || contextTeamId;

  const [teamData, setTeamData] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const billingSummary = billingData?.billingSummary || null;
  const teamBillingTimeZone =
    getNormalizedString(teamData?.teamTimeZone) || undefined;
  const [memberProfiles, setMemberProfiles] = useState({});
  const [pendingProfiles, setPendingProfiles] = useState({});
  const [pendingJoinIds, setPendingJoinIds] = useState([]);
  const [pendingProrateSummary, setPendingProrateSummary] = useState(null);
  const [teamCapabilities, setTeamCapabilities] = useState({});
  const [pageError, setPageError] = useState("");
  const [loading, setLoading] = useState(true);
  const [completingSetup, setCompletingSetup] = useState(false);
  const [savingPayerSelection, setSavingPayerSelection] = useState(false);
  const [autoSavingSoloPayerSetup, setAutoSavingSoloPayerSetup] = useState(false);
  const [approvingPayer, setApprovingPayer] = useState(false);
  const [revokingPayerApproval, setRevokingPayerApproval] = useState(false);
  const [removingPayerApproval, setRemovingPayerApproval] = useState(false);
  const [localSelectedPayers, setLocalSelectedPayers] = useState([]);
  const [localPayerSelectionMode, setLocalPayerSelectionMode] = useState("manual");
  const [showApproveReview, setShowApproveReview] = useState(false);
  const [approveReviewAccepted, setApproveReviewAccepted] = useState(false);
  const [showFinalizeReview, setShowFinalizeReview] = useState(false);
  const [finalizeReviewAccepted, setFinalizeReviewAccepted] = useState(false);

  const hasPendingNextCycleChange =
    billingData?.pendingNextCycleChange === true ||
    teamData?.setupStatus === "active_pending_next_cycle_review";
  const pendingChangeTiming = normalizePendingChangeTiming(
    billingSummary?.pendingChangeTiming || billingData?.pendingChangeTiming
  );
  const renewalGraceState =
    billingSummary?.renewalGraceStateV3 ||
    billingData?.renewalGraceStateV3 ||
    null;
  const billingPauseState =
    billingSummary?.billingPauseStateV3 ||
    billingData?.billingPauseStateV3 ||
    null;
  const expiredApprovalPayerUids = Array.isArray(
    billingSummary?.expiredApprovalPayerUids
  )
    ? billingSummary.expiredApprovalPayerUids
    : [];
  const approvalValidityDays =
    typeof billingSummary?.approvalValidityDays === "number"
      ? billingSummary.approvalValidityDays
      : 14;
  const isBillingHoldRecovery =
    hasPendingNextCycleChange &&
    (
      billingData?.status === "renewal_blocked" ||
      billingData?.status === "renewal_paused" ||
      billingData?.status === "billing_hold" ||
      teamData?.setupStatus === "billing_hold"
    );
  const hasImmediatePendingChange =
    hasPendingNextCycleChange &&
    !isBillingHoldRecovery &&
    pendingChangeTiming === "immediate_prorated";

  const teamName =
    getNormalizedString(teamData?.teamName) ||
    getNormalizedString(teamData?.name) ||
    "";

  const payerStepTimingText = isBillingHoldRecovery
    ? "The previous cycle has ended. Once all selected payers are approved and payment-ready, the team owner can restart paid access."
    : renewalGraceState?.active
      ? `The previous renewal reached its billing boundary. The old split stays active through the grace window until ${formatBillingDate(renewalGraceState.endsAt, teamBillingTimeZone)} while approvals and payment methods are completed.`
    : hasPendingNextCycleChange
      ? hasImmediatePendingChange
        ? "These payer approvals and payment method updates are being prepared for an immediate billing upgrade. Once every selected payer is ready, the plan change applies straight away and only the prorated difference is charged before the next renewal date."
        : "These payer approvals and payment method updates are being prepared for the next billing cycle."
      : "Once all selected payers approve and connect payment methods, the subscription can activate immediately.";


  const currentUid = auth?.currentUser?.uid || null;

  const refreshBilling = useCallback(async () => {
    if (!teamId) return;

    const loadBillingSetup = httpsCallable(
      functions,
      FUNCTION_NAMES.getTeamBillingSetup
    );

    const result = await loadBillingSetup({ teamId });
    const data = result?.data || {};

    setTeamData(data.teamData || null);
    setBillingData({
      ...(data.billingData || {}),
      billingSummary: data.billingSummary || null,
    });
    setTeamCapabilities(data.capabilities || {});
    setMemberProfiles(data.memberProfiles || {});
    setPendingProfiles(data.pendingProfiles || {});
    setPendingJoinIds(Array.isArray(data.pendingJoinIds) ? data.pendingJoinIds : []);
    setPendingProrateSummary(data.pendingProrateSummary || null);
  }, [teamId]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!teamId) {
        setLoading(false);
        setPageError("No active team selected.");
        return;
      }

      try {
        setLoading(true);
        setPageError("");

        const loadBillingSetup = httpsCallable(
          functions,
          FUNCTION_NAMES.getTeamBillingSetup
        );

        const result = await loadBillingSetup({ teamId });
        const data = result?.data || {};

        if (cancelled) return;

        setTeamData(data.teamData || null);
        setBillingData({
          ...(data.billingData || {}),
          billingSummary: data.billingSummary || null,
        });
        setTeamCapabilities(data.capabilities || {});
        setMemberProfiles(data.memberProfiles || {});
        setPendingProfiles(data.pendingProfiles || {});
        setPendingJoinIds(Array.isArray(data.pendingJoinIds) ? data.pendingJoinIds : []);
        setPendingProrateSummary(data.pendingProrateSummary || null);
      } catch (error) {
        console.error("TeamSetupPayers load error:", error);
        if (!cancelled) {
          setPageError(error?.message || "Failed to load payer setup.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    load();

    return () => {
      cancelled = true;
    };
  }, [teamId]);

  const selectedPayers = useMemo(
    () => getSelectedPayersFromBilling(billingData),
    [billingData]
  );

  const payerApprovals = useMemo(
    () => getPayerApprovalsFromBilling(billingData),
    [billingData]
  );

  const paymentMethodsReady = useMemo(
    () => getPaymentMethodsReadyFromBilling(billingData),
    [billingData]
  );

  const payerStripeAccounts = useMemo(
    () => getPayerStripeAccountsFromBilling(billingData),
    [billingData]
  );

  const activePaymentSplitMode = useMemo(
    () => getPaymentSplitModeFromBilling(billingData),
    [billingData]
  );

  const teamMemberIds = useMemo(() => {
    if (!Array.isArray(teamData?.members)) return [];
    return teamData.members.filter(
      (uid) => typeof uid === "string" && uid.trim().length > 0
    );
  }, [teamData]);

  const teamMembers = useMemo(() => {
    return teamMemberIds.map((uid) => ({
      uid,
      profile: memberProfiles[uid] || {},
    }));
  }, [teamMemberIds, memberProfiles]);

  const selectablePayerIds = useMemo(() => {
    return [...new Set([...teamMemberIds, ...pendingJoinIds])];
  }, [teamMemberIds, pendingJoinIds]);

  const selectablePayers = useMemo(() => {
    return selectablePayerIds.map((uid) => ({
      uid,
      profile: memberProfiles[uid] || pendingProfiles[uid] || {},
      isPending: pendingJoinIds.includes(uid),
    }));
  }, [memberProfiles, pendingJoinIds, pendingProfiles, selectablePayerIds]);

  const isSingleMemberTeam = teamMemberIds.length === 1;
  const soloMemberUid = isSingleMemberTeam ? teamMemberIds[0] : null;

  const hasSavedPayerSetup =
    selectedPayers.length > 0 &&
    typeof activePaymentSplitMode === "string" &&
    activePaymentSplitMode.trim().length > 0;

  const isTeamLeader = teamCapabilities.canEditPayerStructure === true;
  const canFinalizeBillingSetup = teamCapabilities.canFinalizeBillingSetup === true;
  const canApproveOwnPayerParticipation =
    teamCapabilities.canApproveOwnPayerParticipation === true;
  const allApproved =
    selectedPayers.length > 0 &&
    selectedPayers.every(
      (uid) => getPayerDecisionStatus(payerApprovals?.[uid] || {}) === "approved"
    );

  const allReady =
    selectedPayers.length > 0 &&
    selectedPayers.every((uid) => paymentMethodsReady?.[uid]?.ready === true);

  const missingPhoneVerificationMembers = useMemo(() => {
    return teamMembers.filter(({ profile }) => profile?.phoneVerified !== true);
  }, [teamMembers]);

  const allPhoneVerified =
    teamMembers.length > 0 && missingPhoneVerificationMembers.length === 0;

  const selectedPayerReviewRows = useMemo(() => {
    return selectedPayers.map((payerUid) => {
      const profile = memberProfiles[payerUid] || pendingProfiles[payerUid] || {};
      const approvalEntry = payerApprovals?.[payerUid] || {};
      const decisionStatus = getPayerDecisionStatus(approvalEntry);
      const paymentReady = paymentMethodsReady?.[payerUid]?.ready === true;

      return {
        uid: payerUid,
        displayName: profile.displayName || payerUid,
        isCurrentUser: payerUid === currentUid,
        approvalStatus:
          decisionStatus === "approved"
            ? "Approved"
            : decisionStatus === "expired"
              ? "Expired"
            : decisionStatus === "revoked"
              ? "Revoked"
              : decisionStatus === "removed"
                ? "Removed"
              : "Pending",
        paymentMethodStatus: paymentReady ? "Ready" : "Pending",
      };
    });
  }, [
    currentUid,
    memberProfiles,
    payerApprovals,
    paymentMethodsReady,
    pendingProfiles,
    selectedPayers,
  ]);

  const isLaunchBasicPromoActive =
    SUBSCRIPTION_PROMO_CONFIG.launchBasicFallbackEnabled === true &&
    SUBSCRIPTION_PROMO_CONFIG.launchBasicSelectableWithoutBilling === true;

  const billingBreakdown = useMemo(() => {
    const resolvedPlanKey =
      getNormalizedString(billingSummary?.planKey) ||
      getNormalizedString(billingData?.planKey);

    if (!resolvedPlanKey || !SUBSCRIPTION_PLANS[resolvedPlanKey]) {
      return null;
    }

    const plan = SUBSCRIPTION_PLANS[resolvedPlanKey];
    const displayCurrency = getResolvedBillingCurrency(billingSummary, billingData);
    const projectedPendingCount = hasPendingNextCycleChange ? pendingJoinIds.length : 0;
    const teamMemberCount = teamMemberIds.length + projectedPendingCount;
    const payerCount = selectedPayers.length;
    const pricingPreview =
      billingSummary?.pricingPreview &&
      typeof billingSummary.pricingPreview === "object"
        ? billingSummary.pricingPreview
        : null;

    const supportsFullTeamMode = plan.supportsFullTeamMode === true;
    const fullTeamModeActive =
      supportsFullTeamMode && teamMemberCount >= FULL_TEAM_MODE_THRESHOLD;

    const billableSeatCount = fullTeamModeActive
      ? Math.min(teamMemberCount, FULL_TEAM_MODE_PRICE_CAP_USERS)
      : teamMemberCount;

    const planUnitPrice = getPlanUnitPriceForCurrency(
      resolvedPlanKey,
      plan,
      displayCurrency,
      isLaunchBasicPromoActive
    );

    const planSubtotal =
      typeof planUnitPrice === "number"
        ? planUnitPrice * billableSeatCount
        : null;

    const selectedStorageAddOnKey = getResolvedStorageAddOnKey(
      billingSummary,
      billingData
    );

    const selectedStorageAddOn = selectedStorageAddOnKey
      ? STORAGE_ADD_ON_OPTIONS.find((option) => option.key === selectedStorageAddOnKey) || null
      : null;

    const storageSubtotal = selectedStorageAddOn
      ? getStoragePriceForCurrency(selectedStorageAddOn, displayCurrency)
      : 0;

    const planSubtotalMinor =
      typeof planSubtotal === "number" ? amountToMinor(planSubtotal) : null;
    const storageSubtotalMinor =
      typeof storageSubtotal === "number" ? amountToMinor(storageSubtotal) : 0;
    const teamTotalMinor =
      typeof planSubtotalMinor === "number"
        ? planSubtotalMinor + storageSubtotalMinor
        : null;

    const perPayerShareMinor =
      typeof teamTotalMinor === "number" && payerCount > 0
        ? Math.floor(teamTotalMinor / payerCount)
        : null;
    const payerCollectedTotalMinor =
      typeof perPayerShareMinor === "number"
        ? perPayerShareMinor * payerCount
        : null;
    const businessAbsorbedRoundingMinor =
      typeof teamTotalMinor === "number" && typeof payerCollectedTotalMinor === "number"
        ? Math.max(0, teamTotalMinor - payerCollectedTotalMinor)
        : null;

    const resolvedTeamTotalMinor =
      typeof pricingPreview?.teamComputedTotalMinor === "number"
        ? pricingPreview.teamComputedTotalMinor
        : teamTotalMinor;
    const resolvedPayerCollectedTotalMinor =
      typeof pricingPreview?.payerCollectedTotalMinor === "number"
        ? pricingPreview.payerCollectedTotalMinor
        : payerCollectedTotalMinor;
    const resolvedBusinessAbsorbedRoundingMinor =
      typeof pricingPreview?.businessAbsorbedRoundingMinor === "number"
        ? pricingPreview.businessAbsorbedRoundingMinor
        : businessAbsorbedRoundingMinor;
    const resolvedPerPayerShareMinor =
      typeof pricingPreview?.equalPerPayerShareMinor === "number"
        ? pricingPreview.equalPerPayerShareMinor
        : perPayerShareMinor;

    const teamTotal = minorToAmount(resolvedTeamTotalMinor);
    const perPayerShare = minorToAmount(resolvedPerPayerShareMinor);

    const currentUserPricingPreview =
      currentUid && Array.isArray(pricingPreview?.payerShares)
        ? pricingPreview.payerShares.find((share) => share?.payerUid === currentUid) || null
        : null;
    const payerSharePreviewByUid =
      Array.isArray(pricingPreview?.payerShares)
        ? Object.fromEntries(
            pricingPreview.payerShares
              .filter((share) => typeof share?.payerUid === "string")
              .map((share) => [share.payerUid, share])
          )
        : {};
    const currentUserShare =
      currentUserPricingPreview &&
      typeof currentUserPricingPreview.discountedRecurringAmountMinor === "number"
        ? minorToAmount(currentUserPricingPreview.discountedRecurringAmountMinor)
        : currentUid && selectedPayers.includes(currentUid)
          ? perPayerShare
          : null;
    const currentUserBaseShare =
      currentUserPricingPreview &&
      typeof currentUserPricingPreview.baseRecurringShareMinor === "number"
        ? minorToAmount(currentUserPricingPreview.baseRecurringShareMinor)
        : null;
    const currentUserPromoDiscount =
      currentUserPricingPreview &&
      typeof currentUserPricingPreview.userPromoDiscountAmountMinor === "number"
        ? minorToAmount(currentUserPricingPreview.userPromoDiscountAmountMinor)
        : null;
    const currentUserPayerStripeRecord =
      currentUid && payerStripeAccounts?.[currentUid]
        ? payerStripeAccounts[currentUid]
        : {};
    const currentUserTaxMinor =
      typeof currentUserPayerStripeRecord?.latestInvoiceTaxMinor === "number"
        ? currentUserPayerStripeRecord.latestInvoiceTaxMinor
        : 0;
    const currentUserHasInvoiceTaxSnapshot =
      typeof currentUserPayerStripeRecord?.latestInvoiceTotalMinor === "number";
    const currentUserTotalWithTax =
      currentUserHasInvoiceTaxSnapshot
        ? minorToAmount(currentUserPayerStripeRecord.latestInvoiceTotalMinor)
        : null;

    const normalizedBillingStatus =
      getNormalizedString(billingSummary?.status) ||
      getNormalizedString(billingData?.status) ||
      "inactive";

    const trialActive = normalizedBillingStatus === "trialing";

    const trialConsumed =
      !trialActive &&
      (billingData?.trialConsumedByTeam === true ||
        billingData?.hasUsedTrial === true);

    const explicitTrialEligibility =
      typeof billingData?.trialEligibility?.eligible === "boolean"
        ? billingData.trialEligibility.eligible
        : typeof billingSummary?.trialEligibility?.eligible === "boolean"
          ? billingSummary.trialEligibility.eligible
          : null;

    const resolvedTrialEligibility =
      typeof explicitTrialEligibility === "boolean"
        ? explicitTrialEligibility
        : typeof billingData?.hasUsedTrial === "boolean"
          ? !billingData.hasUsedTrial
          : typeof billingSummary?.hasUsedTrial === "boolean"
            ? !billingSummary.hasUsedTrial
            : trialConsumed
              ? false
              : true;

    const trialEligibleByPolicy = resolvedTrialEligibility;
    const trialReadyToStart = trialEligibleByPolicy && allPhoneVerified;

    const planLabel =
      plan.label || resolvedPlanKey;

    const billingIntervalLabel =
      typeof billingSummary?.billingInterval === "string" && billingSummary.billingInterval.trim().length > 0
        ? billingSummary.billingInterval
        : typeof billingData?.billingInterval === "string" && billingData.billingInterval.trim().length > 0
          ? billingData.billingInterval
          : "monthly";

    const pricingModeLabel = fullTeamModeActive
      ? "Full-Team-Mode active"
      : supportsFullTeamMode
        ? "Per-user pricing until 5 players"
        : "Standard per-user pricing";

    const currentPeriodEnd =
      billingSummary?.currentPeriodEnd ||
      billingData?.currentPeriodEnd ||
      getLatestPayerCurrentPeriodEnd(payerStripeAccounts) ||
      null;

    const trialStartedAt =
      billingSummary?.trialStartedAt ||
      billingData?.trialStartedAt ||
      null;

    const trialEndsAt =
      billingSummary?.trialEndsAt ||
      billingData?.trialEndsAt ||
      null;

    const trialConsumedEndedAt =
      trialConsumed && trialEndsAt
        ? trialEndsAt
        : trialConsumed
          ? addDaysToBillingDate(trialStartedAt, FREE_TRIAL_DAYS)
          : null;

    const hasLivePaidCycle =
      !hasPendingNextCycleChange &&
      !trialActive &&
      (normalizedBillingStatus === "active" ||
        normalizedBillingStatus === "past_due" ||
        (currentPeriodEnd &&
          (billingSummary?.paymentSetupCompleted === true ||
            billingData?.paymentSetupCompleted === true)));

    const activationTimingLabel = hasPendingNextCycleChange
      ? hasImmediatePendingChange
        ? "Immediate upgrade with proration"
        : "Next billing cycle"
      : hasLivePaidCycle
        ? "Current billing cycle"
        : trialActive
          ? "Current free-trial cycle"
          : "Activates now after payer completion";

    const paymentStartLabel = hasPendingNextCycleChange
      ? hasImmediatePendingChange
        ? currentPeriodEnd
          ? `Approved upgrade activates immediately with proration. Current renewal stays on ${formatBillingDateOnly(currentPeriodEnd, teamBillingTimeZone)}`
          : "Approved upgrade activates immediately with proration once approvals and cards are complete"
        : currentPeriodEnd
          ? `Change applies on ${formatBillingDateOnly(currentPeriodEnd, teamBillingTimeZone)}`
          : "Change applies at the next billing cycle once that date is set"
      : hasLivePaidCycle
        ? currentPeriodEnd
          ? `Next renewal on ${formatBillingDateOnly(currentPeriodEnd, teamBillingTimeZone)}`
          : "Paid billing is currently active."
      : trialActive
        ? trialEndsAt
          ? `Free trial until ${formatBillingDateOnly(trialEndsAt, teamBillingTimeZone)}`
          : "Free trial is currently active."
        : trialReadyToStart
          ? "Free trial starts once all selected payers approve and complete payment setup. Paid billing starts after the free trial ends."
          : "Billing starts once all selected payers approve and complete payment setup.";

    const freeTrialStatusLabel = trialActive
      ? trialEndsAt
        ? `Active until ${formatBillingDateOnly(trialEndsAt, teamBillingTimeZone)}`
        : "Active"
      : trialConsumed
        ? "Already used — next cycle is paid"
      : trialEligibleByPolicy
        ? `Eligible — starts with ${FREE_TRIAL_DAYS}-Day Free Trial`
        : "Not Eligible";

    const resolvedFreeTrialStatusLabel = trialActive
      ? trialEndsAt
        ? `Active until ${formatBillingDateOnly(trialEndsAt, teamBillingTimeZone)}`
        : "Active"
      : trialConsumed
        ? trialConsumedEndedAt
          ? `Free Trial Terminated as of ${formatBillingDateOnly(trialConsumedEndedAt, teamBillingTimeZone)}`
          : "Free Trial Terminated"
        : trialEligibleByPolicy
          ? `Eligible - starts with ${FREE_TRIAL_DAYS}-Day Free Trial`
          : "Not Eligible";

    return {
      planLabel,
      displayCurrency,
      billingIntervalLabel,
      teamMemberCount,
      billableSeatCount,
      payerCount,
      pricingModeLabel,
      selectedStorageAddOn,
      trialActive,
      trialConsumed,
      trialStartedAt,
      trialEndsAt,
      trialConsumedEndedAt,
      trialEligibleByPolicy,
      trialReadyToStart,
      legacyFreeTrialStatusLabel:
        typeof resolvedFreeTrialStatusLabel === "string"
          ? freeTrialStatusLabel.replaceAll("â€”", "-").replaceAll("—", "-")
          : freeTrialStatusLabel,
      freeTrialStatusLabelSanitized:
        typeof resolvedFreeTrialStatusLabel === "string"
          ? resolvedFreeTrialStatusLabel
              .replaceAll("Ã¢â‚¬â€", "-")
              .replaceAll("â€”", "-")
          : resolvedFreeTrialStatusLabel,
      freeTrialStatusLabel: resolvedFreeTrialStatusLabel,
      planSubtotal,
      storageSubtotal,
      teamTotalMinor: resolvedTeamTotalMinor,
      payerCollectedTotalMinor: resolvedPayerCollectedTotalMinor,
      businessAbsorbedRoundingMinor: resolvedBusinessAbsorbedRoundingMinor,
      teamTotal,
      perPayerShareMinor: resolvedPerPayerShareMinor,
      perPayerShare,
      currentUserShare,
      currentUserTaxMinor,
      currentUserHasInvoiceTaxSnapshot,
      currentUserTotalWithTax,
      currentUserBaseShare,
      currentUserPromoDiscount,
      payerSharePreviewByUid,
      appliedTeamPromos: Array.isArray(pricingPreview?.appliedTeamPromos)
        ? pricingPreview.appliedTeamPromos
        : [],
      currentUserAppliedPromos:
        currentUid &&
        pricingPreview?.appliedUserPromosByPayer &&
        typeof pricingPreview.appliedUserPromosByPayer === "object" &&
        Array.isArray(pricingPreview.appliedUserPromosByPayer?.[currentUid])
          ? pricingPreview.appliedUserPromosByPayer[currentUid]
          : [],
      teamBillPromoDiscountMinor:
        typeof pricingPreview?.teamBillPromoDiscountAmountMinor === "number"
          ? pricingPreview.teamBillPromoDiscountAmountMinor
          : 0,
      userBillPromoDiscountMinor:
        typeof pricingPreview?.userBillPromoDiscountAmountMinor === "number"
          ? pricingPreview.userBillPromoDiscountAmountMinor
          : 0,
      totalPromoDiscountMinor:
        typeof pricingPreview?.totalPromoDiscountAmountMinor === "number"
          ? pricingPreview.totalPromoDiscountAmountMinor
          : 0,
      activationTimingLabel,
      paymentStartLabel,
      currentPeriodEnd,
    };
  }, [
    billingSummary,
    billingData,
    teamMemberIds,
    pendingJoinIds,
    selectedPayers,
    currentUid,
    isLaunchBasicPromoActive,
    hasPendingNextCycleChange,
    hasImmediatePendingChange,
    allPhoneVerified,
    payerStripeAccounts,
    teamBillingTimeZone,
  ]);

  const countedBillingMemberIds = useMemo(() => {
    return hasPendingNextCycleChange
      ? [...new Set([...teamMemberIds, ...pendingJoinIds])]
      : teamMemberIds;
  }, [hasPendingNextCycleChange, pendingJoinIds, teamMemberIds]);

  const countedBillingMemberNames = useMemo(() => {
    const profilesByUid = {
      ...memberProfiles,
      ...pendingProfiles,
    };
    return buildDisplayNameList(countedBillingMemberIds, profilesByUid, currentUid);
  }, [countedBillingMemberIds, currentUid, memberProfiles, pendingProfiles]);

  const selectedPayerNames = useMemo(() => {
    const profilesByUid = {
      ...memberProfiles,
      ...pendingProfiles,
    };
    return buildDisplayNameList(selectedPayers, profilesByUid, currentUid);
  }, [currentUid, memberProfiles, pendingProfiles, selectedPayers]);

  const countedMembersTooltip = countedBillingMemberNames.join("\n");
  const selectedPayersTooltip = selectedPayerNames.join("\n");
  const billableSeatsTooltip =
    billingBreakdown &&
    billingBreakdown.billableSeatCount === countedBillingMemberNames.length
      ? countedMembersTooltip
      : billingBreakdown
        ? `This plan currently bills for ${billingBreakdown.billableSeatCount} seats based on the team's current roster and plan rules. The cap is plan-based and not assigned to specific usernames.`
        : "";

  const liveBillingStatus =
    getNormalizedString(billingSummary?.status) ||
    getNormalizedString(billingData?.status) ||
    getNormalizedString(teamData?.setupStatus) ||
    "";
  const normalizedLiveBillingStatus = liveBillingStatus.toLowerCase();
  const isCurrentSubscriptionTrialing =
    !hasPendingNextCycleChange &&
    !isBillingHoldRecovery &&
    normalizedLiveBillingStatus === "trialing";
  const isCurrentSubscriptionActive =
    !hasPendingNextCycleChange &&
    !isBillingHoldRecovery &&
    normalizedLiveBillingStatus === "active";
  const isCurrentSubscriptionLive =
    isCurrentSubscriptionTrialing || isCurrentSubscriptionActive;

  const canFinalizeNow =
    !isCurrentSubscriptionLive &&
    hasSavedPayerSetup &&
    allApproved &&
    allReady &&
    !completingSetup;

  const shouldStartWithFreeTrial =
    canFinalizeNow && billingBreakdown?.trialReadyToStart === true;

  const finalizeButtonLabel = completingSetup
    ? isBillingHoldRecovery
      ? "Restarting..."
      : hasPendingNextCycleChange
      ? hasImmediatePendingChange
        ? "Activating upgrade..."
        : "Saving..."
      : "Activating..."
    : isBillingHoldRecovery
      ? "Restart Team Subscription"
      : hasPendingNextCycleChange
      ? hasImmediatePendingChange
        ? "Activate Upgrade Now"
        : "Save Next Billing Setup"
      : shouldStartWithFreeTrial
        ? "Start Your Team Free Trial Now"
        : "Start Your Subscription Now";

  const finalizationCardKicker = isCurrentSubscriptionTrialing
    ? "Free trial active"
    : isCurrentSubscriptionActive
      ? "Subscription active"
      : isBillingHoldRecovery
        ? "Ready to restart"
        : hasPendingNextCycleChange
          ? hasImmediatePendingChange
            ? "Ready to activate now"
            : "Ready for next cycle"
          : "Ready to activate";

  const finalizationCardCopy = isCurrentSubscriptionTrialing
    ? billingBreakdown?.trialEndsAt
      ? `Your team subscription is active on a free trial. Paid billing begins after the trial ends on ${formatBillingDate(billingBreakdown.trialEndsAt, teamBillingTimeZone)}.`
      : "Your team subscription is active on a free trial. Paid billing begins after the trial ends."
    : isCurrentSubscriptionActive
      ? "Your team subscription is active. Use billing overview to manage your card, payer responsibilities, and subscription status."
    : isBillingHoldRecovery
      ? "The previous billing cycle has ended. Once all selected payers are approved and payment-ready, the team owner can restart paid access by starting a new cycle now."
      : hasPendingNextCycleChange
          ? hasImmediatePendingChange
            ? "Once all selected payers are approved and payment-ready, the team owner can activate this billing upgrade right away. Only the prorated difference is charged before the current renewal date."
            : "Team owner can save the next billing setup once all selected payers are approved and payment-ready. The current cycle stays unchanged until renewal."
          : "Team owner can finalize once all selected payers are approved and payment-ready.";

  const reviewNow = useMemo(() => {
    const recomputeAtReviewToggle = showFinalizeReview;
    return recomputeAtReviewToggle ? new Date() : new Date();
  }, [showFinalizeReview]);
  const reviewTrialStartsAt = reviewNow;
  const reviewTrialEndsAt =
    shouldStartWithFreeTrial
      ? billingBreakdown?.trialEndsAt || addDays(reviewNow, FREE_TRIAL_DAYS)
      : null;
  const reviewFirstBillingDate = shouldStartWithFreeTrial
    ? reviewTrialEndsAt
    : isBillingHoldRecovery
      ? reviewNow
      : hasPendingNextCycleChange
      ? billingBreakdown?.currentPeriodEnd || billingSummary?.currentPeriodEnd || reviewNow
      : reviewNow;

  useEffect(() => {
    if (selectedPayers.length > 0) {
      setLocalSelectedPayers(selectedPayers);
      return;
    }

    if (isSingleMemberTeam && soloMemberUid) {
      setLocalSelectedPayers([soloMemberUid]);
    }
  }, [selectedPayers, isSingleMemberTeam, soloMemberUid]);

  useEffect(() => {
    if (activePaymentSplitMode) {
      setLocalPayerSelectionMode(
        billingSummary?.payerSelectionMode || billingData?.payerSelectionMode || "manual"
      );
    }
  }, [activePaymentSplitMode, billingData?.payerSelectionMode, billingSummary?.payerSelectionMode]);

  useEffect(() => {
    if (localPayerSelectionMode === "all_team_members") {
      setLocalSelectedPayers(selectablePayerIds);
    }
  }, [localPayerSelectionMode, selectablePayerIds]);

  useEffect(() => {
    if (
      !teamId ||
      !isTeamLeader ||
      !isSingleMemberTeam ||
      !soloMemberUid ||
      hasSavedPayerSetup ||
      autoSavingSoloPayerSetup
    ) {
      return;
    }

    let cancelled = false;

    async function autoSaveSoloPayerSetup() {
      try {
        setAutoSavingSoloPayerSetup(true);
        setPageError("");

        const saveTeamPayers = httpsCallable(
          functions,
          FUNCTION_NAMES.saveTeamPayers
        );

        await saveTeamPayers({
          teamId,
          selectedPayers: [soloMemberUid],
          paymentSplitMode: "equal_split",
        });

        if (!cancelled) {
          await refreshBilling();
        }
      } catch (error) {
        console.error("autoSaveSoloPayerSetup error:", error);
        if (!cancelled) {
          setPageError(
            error?.message || "Failed to automatically configure payer setup."
          );
        }
      } finally {
        if (!cancelled) {
          setAutoSavingSoloPayerSetup(false);
        }
      }
    }

    autoSaveSoloPayerSetup();

    return () => {
      cancelled = true;
    };
  }, [
    teamId,
    isTeamLeader,
    isSingleMemberTeam,
    soloMemberUid,
    hasSavedPayerSetup,
    autoSavingSoloPayerSetup,
    refreshBilling,
  ]);

  const handleToggleLocalPayer = (payerUid) => {
    if (localPayerSelectionMode === "all_team_members") {
      return;
    }
    setLocalSelectedPayers((prev) => {
      if (prev.includes(payerUid)) {
        return prev.filter((uid) => uid !== payerUid);
      }

      return [...prev, payerUid];
    });
  };

  const handleSelectAllTeamMembersPayers = () => {
    setLocalPayerSelectionMode("all_team_members");
    setLocalSelectedPayers(selectablePayerIds);
  };

  const handleSavePayerSelection = async () => {
    setPageError("");

    if (!teamId) {
      setPageError("Missing team.");
      return;
    }

    if (!isTeamLeader) {
      setPageError("Only the team owner can save payer setup.");
      return;
    }

    if (!Array.isArray(localSelectedPayers) || localSelectedPayers.length === 0) {
      setPageError("Select at least one payer.");
      return;
    }

    try {
      setSavingPayerSelection(true);

      const saveTeamPayers = httpsCallable(
        functions,
        FUNCTION_NAMES.saveTeamPayers
      );

      await saveTeamPayers({
        teamId,
        selectedPayers: localSelectedPayers,
        paymentSplitMode: "equal_split",
        payerSelectionMode: localPayerSelectionMode,
      });

      await refreshBilling();
    } catch (error) {
      console.error("saveTeamPayers error:", error);
      setPageError(error?.message || "Failed to save payer setup.");
    } finally {
      setSavingPayerSelection(false);
    }
  };

  const handleApprove = async () => {
    setPageError("");

    if (!canApproveOwnPayerParticipation) {
      setPageError("You are not one of the selected payers for this billing cycle.");
      return;
    }

    if (!hasSavedPayerSetup) {
      setPageError("Save payer setup before approving your payer participation.");
      return;
    }

    setApproveReviewAccepted(false);
    setShowApproveReview(true);
  };

  const handleCloseApproveReview = () => {
    if (approvingPayer) return;
    setShowApproveReview(false);
    setApproveReviewAccepted(false);
  };

  const handleConfirmApprove = async () => {
    setPageError("");

    if (!canApproveOwnPayerParticipation) {
      setPageError("You are not one of the selected payers for this billing cycle.");
      return;
    }

    if (!approveReviewAccepted) {
      setPageError(
        "Please confirm your payer participation and accept the Terms of Service and Privacy Policy before approving."
      );
      return;
    }

    try {
      setApprovingPayer(true);

      const approveFn = httpsCallable(functions, FUNCTION_NAMES.approveTeamPayer);
      await approveFn({ teamId });
      await refreshBilling();
      setShowApproveReview(false);
      setApproveReviewAccepted(false);
      window.dispatchEvent(new Event("payer-action-overlay:refresh"));
    } catch (error) {
      console.error("approveTeamPayer error:", error);
      setPageError(error?.message || "Failed to approve payer participation.");
    } finally {
      setApprovingPayer(false);
    }
  };

  const handleRemoveApproval = async () => {
    setPageError("");

    try {
      setRemovingPayerApproval(true);

      const removeFn = httpsCallable(
        functions,
        FUNCTION_NAMES.removeTeamPayerApproval
      );

      await removeFn({ teamId });
      await refreshBilling();
    } catch (error) {
      console.error("removeTeamPayerApproval error:", error);
      setPageError(error?.message || "Failed to remove approval.");
    } finally {
      setRemovingPayerApproval(false);
    }
  };

  const handleRevoke = async () => {
    setPageError("");

    if (!canApproveOwnPayerParticipation) {
      setPageError("You are not one of the selected payers for this billing cycle.");
      return;
    }

    try {
      setRevokingPayerApproval(true);

      const revokeFn = httpsCallable(
        functions,
        FUNCTION_NAMES.revokeTeamPayerApproval
      );
      await revokeFn({ teamId });
      await refreshBilling();
      window.dispatchEvent(new Event("payer-action-overlay:refresh"));
    } catch (error) {
      console.error("revokeTeamPayerApproval error:", error);
      setPageError(error?.message || "Failed to revoke approval.");
    } finally {
      setRevokingPayerApproval(false);
    }
  };

  const handleOpenFinalizeReview = () => {
    setPageError("");
    setFinalizeReviewAccepted(false);
    setShowFinalizeReview(true);
  };

  const handleCloseFinalizeReview = () => {
    if (completingSetup) return;
    setShowFinalizeReview(false);
    setFinalizeReviewAccepted(false);
  };

  const handleComplete = async () => {
    setPageError("");

    if (!finalizeReviewAccepted) {
      setPageError(
        "Please confirm the billing details and accept the Terms of Service and Privacy Policy before continuing."
      );
      return;
    }

    const missingNames = missingPhoneVerificationMembers.map(
      ({ uid, profile }) => profile?.displayName || uid
    );

    if (billingBreakdown?.trialReadyToStart !== true && missingNames.length > 0) {
      setPageError(
        `The subscription will start without a free trial because these users have not completed phone verification: ${missingNames.join(", ")}.`
      );
    }

    try {
      setCompletingSetup(true);

      const completeFn = httpsCallable(
        functions,
        isBillingHoldRecovery
          ? FUNCTION_NAMES.processTeamRenewal
          : FUNCTION_NAMES.completeTeamPaymentSetup
      );

      await completeFn({ teamId });

      await refreshBilling();
      if (typeof refreshAccess === "function") {
        await refreshAccess();
      }
      setShowFinalizeReview(false);
      setFinalizeReviewAccepted(false);
      navigate("/team-center/dashboard");
    } catch (error) {
      console.error(
        isBillingHoldRecovery
          ? "processTeamRenewal error:"
          : "completeTeamPaymentSetup error:",
        error
      );
      setPageError(
        error?.message ||
          (isBillingHoldRecovery
            ? "Failed to restart the team subscription."
            : "Failed to complete billing setup.")
      );
    } finally {
      setCompletingSetup(false);
    }
  };

  if (loading) {
    return (
      <TeamCenterFrame
        title="Team Payment Setup"
        identityLabel="Team Payment Setup"
        showPageTitle={false}
        showTeamFacts
        description="Preparing payer approvals and payment methods for this team."
        footer={<Footer />}
      >
        <div className="team-setup-payers-page TeamCenterPayersPage">
          <div className="team-setup-payers-page__main">
            <div className="team-setup-payers team-setup-payers--loading">
              <div className="team-setup-payers__shell team-setup-payers__shell--loading">
              <section className="team-setup-payers__layout">
                <div className="team-setup-payers__main">
                  <div className="team-setup-payers__summary-card" />
                  <div className="team-setup-payers__summary-card" />
                  <div className="team-setup-payers__summary-card" />
                </div>

                <aside className="team-setup-payers__sidebar">
                  <div className="team-setup-payers__summary-card team-setup-payers__summary-card--loading-fill" />
                </aside>
              </section>
            </div>
          </div>
          </div>
        </div>
      </TeamCenterFrame>
    );
  }

  return (
    <TeamCenterFrame
      title="Team Payment Setup"
      identityLabel="Team Payment Setup"
      showPageTitle={false}
      showTeamFacts
      description={payerStepTimingText}
      actions={(
        <>
          <button
            type="button"
            onClick={() => navigate(buildBillingCenterPath(teamId))}
          >
            Billing Center
          </button>
          <button
            type="button"
            onClick={refreshBilling}
            aria-label="Refresh payer setup"
            title="Refresh payer setup"
          >
            <RefreshCw size={17} />
          </button>
        </>
      )}
      footer={<Footer />}
    >
      <div className="team-setup-payers-page TeamCenterPayersPage">
        <div className="team-setup-payers-page__main">
          <div className="team-setup-payers">
            <div className="team-setup-payers__shell">
              {!isTeamLeader ? (
                <section className="team-setup-payers__top-band TeamCenterPayersPage__context">
                  <div className="team-setup-payers__summary-note">
                    View-only for team-wide billing controls. Only the team owner can change payer structure or complete the team subscription. Selected payers can still approve their own participation and connect their own card.
                  </div>
                </section>
              ) : null}

            {pageError ? (
              <div className="team-setup-payers__error">{pageError}</div>
            ) : null}

            {billingPauseState?.active ? (
              <div className="team-setup-payers__summary-note" style={{ marginBottom: "1rem" }}>
                Billing is currently paused. Access resumes after all selected payers reapprove this split and payment setup is complete again.
              </div>
            ) : null}

            {renewalGraceState?.active ? (
              <div className="team-setup-payers__summary-note" style={{ marginBottom: "1rem" }}>
                The team is inside the 3-day billing grace period until{" "}
                {formatBillingDate(renewalGraceState.endsAt, teamBillingTimeZone)}.
              </div>
            ) : null}

            <section className="team-setup-payers__layout">
              <div className="team-setup-payers__main">
                {!hasSavedPayerSetup ? (
                  <div className="team-setup-payers__summary-card">
                    <div className="team-setup-payers__summary-kicker">Choose payers</div>
                    <div className="team-setup-payers__summary-copy">
                      Select who will pay for this subscription and save the payer setup before approvals and payment methods.
                    </div>

                    {autoSavingSoloPayerSetup ? (
                      <div className="team-setup-payers__summary-note">
                        Auto-configuring payer setup for your single-member team...
                      </div>
                    ) : isSingleMemberTeam ? (
                      <div className="team-setup-payers__storage-addon-box">
                        <div className="team-setup-payers__storage-addon-title">
                          Single-member team
                        </div>
                        <div className="team-setup-payers__storage-addon-copy">
                          The only team member will be used as the payer automatically.
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="team-setup-payers__summary-row team-setup-payers__summary-row--stack">
                          <label className="team-setup-payers__field-label">
                            Select payers
                          </label>

                          <button
                            type="button"
                            className="team-setup-payers__button team-setup-payers__button--secondary"
                            onClick={handleSelectAllTeamMembersPayers}
                            title={
                              hasImmediatePendingChange
                                ? "Automatically selects every current team member for this upgrade. Everyone affected will still need to approve their own share and connect a ready payment method."
                                : "Automatically selects every current team member and every pending join for the next billing cycle. Everyone affected will still need to approve the next-cycle split."
                            }
                            style={{ marginBottom: "0.75rem" }}
                          >
                            On This Team Everyone Pays An Equal Share
                          </button>

                          {localPayerSelectionMode === "all_team_members" ? (
                            <div className="team-setup-payers__summary-note">
                              Payers auto-sync with team membership while this mode is active.
                              Adding or removing members can reopen payer approvals and payment
                              setup if the billing split changes.
                            </div>
                          ) : null}

                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              gap: "0.75rem",
                            }}
                          >
                            {selectablePayers.map(({ uid, profile, isPending }) => (
                              <label
                                key={uid}
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "0.75rem",
                                  cursor: "pointer",
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={localSelectedPayers.includes(uid)}
                                  onChange={() => handleToggleLocalPayer(uid)}
                                  disabled={localPayerSelectionMode === "all_team_members"}
                                />
                                <span>
                                  {profile.displayName || uid}
                                  {isPending ? " (Pending join)" : ""}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>

                        <div className="team-setup-payers__summary-row team-setup-payers__summary-row--stack">
                          <label className="team-setup-payers__field-label">
                            Payment split mode
                          </label>

                          <div className="team-setup-payers__summary-note">
                            Payments are always split equally across the selected payers.
                          </div>
                        </div>
                      </>
                    )}

                    {isTeamLeader ? (
                      <button
                        type="button"
                        className="team-setup-payers__button"
                        disabled={
                          savingPayerSelection ||
                          autoSavingSoloPayerSetup ||
                          (isSingleMemberTeam
                            ? true
                            : localSelectedPayers.length === 0)
                        }
                        onClick={handleSavePayerSelection}
                      >
                        {savingPayerSelection ? "Saving Payer Setup..." : "Save Payer Setup"}
                      </button>
                    ) : (
                      <div className="team-setup-payers__summary-note">
                        Only the team owner can choose and save payers.
                      </div>
                    )}
                  </div>
                ) : null}

                <div className="team-setup-payers__summary-card">
                  <div className="team-setup-payers__summary-kicker">Selected payers</div>
                  <div className="team-setup-payers__summary-copy">
                    Everyone listed below must approve their own payer participation. Each payer must also connect their own card from their own account.
                  </div>

                  {expiredApprovalPayerUids.length > 0 ? (
                    <div className="team-setup-payers__summary-note" style={{ marginTop: "0.75rem" }}>
                      {expiredApprovalPayerUids.length} payer approval
                      {expiredApprovalPayerUids.length === 1 ? "" : "s"} expired and must be reconfirmed before billing can proceed.
                    </div>
                  ) : null}

                  <div className="team-setup-payers__summary-row team-setup-payers__summary-row--stack">
                    {selectedPayers.length === 0 ? (
                      <div className="team-setup-payers__summary-note">
                        Save payer setup first.
                      </div>
                    ) : (
                      selectedPayers.map((payerUid) => {
                        const profile = memberProfiles[payerUid] || {};
                        const approvalEntry = payerApprovals?.[payerUid] || {};
                        const decisionStatus = getPayerDecisionStatus(approvalEntry);
                        const approved = decisionStatus === "approved";
                        const expired = decisionStatus === "expired";
                        const revoked = decisionStatus === "revoked";
                        const ready = paymentMethodsReady?.[payerUid]?.ready === true;
                        const payerStripe = payerStripeAccounts?.[payerUid] || {};
                        const isCurrentUserPayerRow = payerUid === currentUid;
                        const approvalExpiresAt = approvalEntry?.approvalExpiresAt || null;

                        return (
                          <div
                            key={payerUid}
                            className="team-setup-payers__storage-addon-box"
                          >
                            <div className="team-setup-payers__storage-addon-title">
                              {profile.displayName || payerUid}
                              {isCurrentUserPayerRow ? " (You)" : ""}
                            </div>

                            <div className="team-setup-payers__storage-addon-copy">
                              Approval: {
                                approved
                                  ? "Approved"
                                  : expired
                                    ? "Expired"
                                  : revoked
                                    ? "Revoked"
                                    : decisionStatus === "removed"
                                      ? "Removed"
                                    : "Pending"
                              }
                            </div>

                            <div className="team-setup-payers__storage-addon-copy">
                              Approval Window: {
                                approved || expired
                                  ? formatBillingDate(approvalExpiresAt, teamBillingTimeZone)
                                  : `Valid for ${approvalValidityDays} days after approval`
                              }
                            </div>

                            <div className="team-setup-payers__storage-addon-copy">
                              Payment Method: {ready ? "Ready" : "Pending"}
                            </div>

                            <div className="team-setup-payers__storage-addon-copy">
                              Stripe Customer: {payerStripe?.stripeCustomerId ? "Created" : "Not created yet"}
                            </div>

                            {isCurrentUserPayerRow ? (
                              decisionStatus === "approved" ? (
                                <>
                                  <div className="team-setup-payers__payer-actions">
                                    <button
                                      type="button"
                                      className="team-setup-payers__button team-setup-payers__button--danger"
                                      onClick={handleRemoveApproval}
                                      disabled={
                                        !hasSavedPayerSetup ||
                                        approvingPayer ||
                                        revokingPayerApproval ||
                                        removingPayerApproval
                                      }
                                    >
                                      {removingPayerApproval ? (
                                        <span className="team-setup-payers__button-content">
                                          <span className="team-setup-payers__button-spinner" />
                                          Removing...
                                        </span>
                                      ) : (
                                        "Remove Approval"
                                      )}
                                    </button>

                                    <button
                                      type="button"
                                      className="team-setup-payers__button team-setup-payers__button--danger"
                                      onClick={handleRevoke}
                                      disabled={
                                        !hasSavedPayerSetup ||
                                        approvingPayer ||
                                        revokingPayerApproval ||
                                        removingPayerApproval
                                      }
                                    >
                                      {revokingPayerApproval ? (
                                        <span className="team-setup-payers__button-content">
                                          <span className="team-setup-payers__button-spinner team-setup-payers__button-spinner--secondary" />
                                          Revoking...
                                        </span>
                                      ) : (
                                        "Revoke Approval"
                                      )}
                                    </button>
                                  </div>
                                </>
                              ) : (
                                <>
                                  <button
                                    type="button"
                                    className="team-setup-payers__button team-setup-payers__button--muted"
                                    onClick={handleApprove}
                                    disabled={
                                      !hasSavedPayerSetup ||
                                      approvingPayer ||
                                      revokingPayerApproval ||
                                      removingPayerApproval
                                    }
                                  >
                                    {approvingPayer ? (
                                      <span className="team-setup-payers__button-content">
                                        <span className="team-setup-payers__button-spinner" />
                                        Approving...
                                      </span>
                                    ) : (
                                      "Approve"
                                    )}
                                  </button>

                                  <button
                                    type="button"
                                    className="team-setup-payers__button team-setup-payers__button--secondary"
                                    onClick={handleRevoke}
                                    disabled={
                                      !hasSavedPayerSetup ||
                                      approvingPayer ||
                                      revokingPayerApproval ||
                                      removingPayerApproval
                                    }
                                  >
                                    {revokingPayerApproval ? (
                                      <span className="team-setup-payers__button-content">
                                        <span className="team-setup-payers__button-spinner team-setup-payers__button-spinner--secondary" />
                                        Revoking...
                                      </span>
                                    ) : revoked ? (
                                      "Revoked"
                                    ) : (
                                      "Revoke"
                                    )}
                                  </button>
                                </>
                              )
                            ) : (
                              <div className="team-setup-payers__summary-note">
                                Only this payer can approve, revoke, or manage their own payment step.
                              </div>
                            )}
                          </div>
                        );
                      })
                    )}
                  </div>
                </div>

                {billingBreakdown ? (
                  <TrialVerificationCard
                    currentUid={currentUid}
                    teamMembers={teamMembers}
                    memberProfiles={memberProfiles}
                    billingBreakdown={billingBreakdown}
                    teamBillingTimeZone={teamBillingTimeZone}
                    onRefresh={refreshBilling}
                  />
                ) : null}

                {billingBreakdown ? (
                  <div className="team-setup-payers__summary-card">
                    <div className="team-setup-payers__summary-kicker">Billing breakdown</div>

                    <div className="team-setup-payers__summary-plan">
                      {billingBreakdown.planLabel}
                    </div>

                    <p className="team-setup-payers__summary-copy">
                      This is the estimated recurring team cost based on the current plan, team size, and payer split.
                    </p>

                    <div className="team-setup-payers__summary-note">
                      Taxes are added on top of these recurring prices based on
                      each payer&apos;s billing address. The values below are
                      pre-tax estimates.
                    </div>

                    <div className="team-setup-payers__line-list">
                      <div className="team-setup-payers__line-item">
                        <span className="team-setup-payers__line-label">Billing interval</span>
                        <span className="team-setup-payers__line-value">
                          {billingBreakdown.billingIntervalLabel}
                        </span>
                      </div>

                      <div className="team-setup-payers__line-item">
                        <span className="team-setup-payers__line-label">When this applies</span>
                        <span className="team-setup-payers__line-value">
                          {billingBreakdown.activationTimingLabel}
                        </span>
                      </div>

                      <div className="team-setup-payers__line-item">
                        <span className="team-setup-payers__line-label">Free trial</span>
                        <span
                          className={`team-setup-payers__line-value ${
                            billingBreakdown.trialActive ||
                            billingBreakdown.trialEligibleByPolicy
                              ? "team-setup-payers__line-value--trial-eligible"
                              : "team-setup-payers__line-value--trial-ineligible"
                          }`}
                        >
                          {billingBreakdown.freeTrialStatusLabel}
                        </span>
                      </div>

                      <div className="team-setup-payers__line-item">
                        <span className="team-setup-payers__line-label">Payment timing</span>
                        <span className="team-setup-payers__line-value">
                          {billingBreakdown.paymentStartLabel}
                        </span>
                      </div>

                      <div className="team-setup-payers__line-item">
                        <span className="team-setup-payers__line-label">Pricing mode</span>
                        <span className="team-setup-payers__line-value">
                          {billingBreakdown.pricingModeLabel}
                        </span>
                      </div>

                      <div className="team-setup-payers__line-item">
                        <span className="team-setup-payers__line-label">Team members counted</span>
                        <span
                          className={`team-setup-payers__line-value ${
                            countedMembersTooltip
                              ? "team-setup-payers__line-value--interactive"
                              : ""
                          }`}
                          title={countedMembersTooltip || undefined}
                        >
                          {billingBreakdown.teamMemberCount}
                        </span>
                      </div>

                      <div className="team-setup-payers__line-item">
                        <span className="team-setup-payers__line-label">Billable seats</span>
                        <span
                          className={`team-setup-payers__line-value ${
                            billableSeatsTooltip
                              ? "team-setup-payers__line-value--interactive"
                              : ""
                          }`}
                          title={billableSeatsTooltip || undefined}
                        >
                          {billingBreakdown.billableSeatCount}
                        </span>
                      </div>

                      <div className="team-setup-payers__line-item">
                        <span className="team-setup-payers__line-label">Plan subtotal</span>
                        <span className="team-setup-payers__line-value">
                          {formatMoneyAmount(
                            billingBreakdown.planSubtotal,
                            billingBreakdown.displayCurrency
                          )}
                        </span>
                      </div>

                      <div className="team-setup-payers__line-item">
                        <span className="team-setup-payers__line-label">Storage add-on</span>
                        <span className="team-setup-payers__line-value">
                          {billingBreakdown.selectedStorageAddOn
                            ? formatMoneyAmount(
                                billingBreakdown.storageSubtotal,
                                billingBreakdown.displayCurrency
                              )
                            : "None"}
                        </span>
                      </div>

                      {billingBreakdown.teamBillPromoDiscountMinor > 0 ? (
                        <div className="team-setup-payers__line-item">
                          <span className="team-setup-payers__line-label">Team discount</span>
                          <span className="team-setup-payers__line-value team-setup-payers__line-value--trial-eligible">
                            -{formatMoneyAmount(
                              billingBreakdown.teamBillPromoDiscountMinor / 100,
                              billingBreakdown.displayCurrency
                            )} / month
                          </span>
                        </div>
                      ) : null}

                      {billingBreakdown.userBillPromoDiscountMinor > 0 ? (
                        <div className="team-setup-payers__line-item">
                          <span className="team-setup-payers__line-label">Payer-bill discounts</span>
                          <span className="team-setup-payers__line-value team-setup-payers__line-value--trial-eligible">
                            -{formatMoneyAmount(
                              billingBreakdown.userBillPromoDiscountMinor / 100,
                              billingBreakdown.displayCurrency
                            )} / month
                          </span>
                        </div>
                      ) : null}

                      <div className="team-setup-payers__line-item team-setup-payers__line-item--total">
                        <span className="team-setup-payers__line-label">Estimated pre-tax team total</span>
                        <span className="team-setup-payers__line-value">
                          {formatMoneyAmount(
                            billingBreakdown.teamTotal,
                            billingBreakdown.displayCurrency
                          )} / month
                        </span>
                      </div>

                      <div className="team-setup-payers__line-item">
                        <span className="team-setup-payers__line-label">Selected payers</span>
                        <span
                          className={`team-setup-payers__line-value ${
                            selectedPayersTooltip
                              ? "team-setup-payers__line-value--interactive"
                              : ""
                          }`}
                          title={selectedPayersTooltip || undefined}
                        >
                          {billingBreakdown.payerCount || "—"}
                        </span>
                      </div>

                      <div className="team-setup-payers__line-item">
                        <span className="team-setup-payers__line-label">
                          Estimated pre-tax per-payer share
                        </span>
                        <span className="team-setup-payers__line-value">
                          {billingBreakdown.payerCount > 0
                            ? `${formatMoneyAmount(
                                billingBreakdown.perPayerShare,
                                billingBreakdown.displayCurrency
                              )} / month`
                            : "Save payer setup first"}
                        </span>
                      </div>
                    </div>

                    {billingBreakdown.businessAbsorbedRoundingMinor > 0 ? (
                      <div className="team-setup-payers__note-box">
                        Equal-share billing is rounded down to keep every payer charged the same amount.{" "}
                        {formatMoneyAmount(
                          billingBreakdown.businessAbsorbedRoundingMinor / 100,
                          billingBreakdown.displayCurrency
                        )}{" "}
                        is absorbed by the business for this cycle.
                      </div>
                    ) : null}

                    {typeof billingBreakdown.currentUserShare === "number" ? (
                      <div className="team-setup-payers__note-box">
                        <div>
                          <strong>Your estimated pre-tax share:</strong>{" "}
                          {formatMoneyAmount(
                            billingBreakdown.currentUserShare,
                            billingBreakdown.displayCurrency
                          )}{" "}
                          / month
                        </div>
                        {billingBreakdown.currentUserHasInvoiceTaxSnapshot ? (
                          <div>
                            <strong>Your latest invoice total including tax:</strong>{" "}
                            {formatMoneyAmount(
                              billingBreakdown.currentUserTotalWithTax,
                              billingBreakdown.displayCurrency
                            )}{" "}
                            / month
                          </div>
                        ) : (
                          <div>
                            <strong>Tax status:</strong>{" "}
                            Tax has not been calculated yet. Stripe calculates tax
                            from your billing address when the team subscription
                            is started and an invoice is created.
                          </div>
                        )}
                        {typeof billingBreakdown.currentUserPromoDiscount === "number" &&
                        billingBreakdown.currentUserPromoDiscount > 0 &&
                        typeof billingBreakdown.currentUserBaseShare === "number" ? (
                          <>
                            {" "}
                            (base {formatMoneyAmount(
                              billingBreakdown.currentUserBaseShare,
                              billingBreakdown.displayCurrency
                            )} minus your promo discount of{" "}
                            {formatMoneyAmount(
                              billingBreakdown.currentUserPromoDiscount,
                              billingBreakdown.displayCurrency
                            )})
                          </>
                        ) : null}
                      </div>
                    ) : null}

                    {billingBreakdown.appliedTeamPromos?.length > 0 ||
                    billingBreakdown.currentUserAppliedPromos?.length > 0 ? (
                      <div className="team-setup-payers__note-box">
                        {billingBreakdown.appliedTeamPromos?.map((promo) => (
                          <div key={`team-promo-${promo.code}`}>
                            <strong>{promo.displayName || promo.code}:</strong>{" "}
                            {formatPromoBenefit(promo, billingBreakdown.displayCurrency)} •{" "}
                            {formatPromoDetails(promo, teamBillingTimeZone)}
                          </div>
                        ))}
                        {billingBreakdown.currentUserAppliedPromos?.map((promo) => (
                          <div key={`user-promo-${promo.code}`}>
                            <strong>{promo.displayName || promo.code}:</strong>{" "}
                            {formatPromoBenefit(promo, billingBreakdown.displayCurrency)} •{" "}
                            {formatPromoDetails(promo, teamBillingTimeZone)}
                          </div>
                        ))}
                      </div>
                    ) : null}

                    <div className="team-setup-payers__note">
                      {shouldStartWithFreeTrial
                        ? `Due today: ${formatMoneyAmount(0, billingBreakdown.displayCurrency)}. Recurring billing begins after the free trial ends.`
                        : isCurrentSubscriptionLive
                          ? billingBreakdown.currentPeriodEnd
                            ? `Your current billing cycle renews on ${formatBillingDateOnly(billingBreakdown.currentPeriodEnd, teamBillingTimeZone)}.`
                            : "Your team subscription is currently active."
                        : isBillingHoldRecovery
                          ? "Saving a payment method does not charge the card immediately. Once the team owner confirms the restart, a new paid cycle begins and access is restored."
                          : hasPendingNextCycleChange
                            ? hasImmediatePendingChange
                              ? "Saving a payment method does not charge the card immediately. Once every selected payer is ready, the approved upgrade can activate right away and only the prorated difference is charged before renewal."
                              : "Saving a payment method does not charge the card immediately. The current cycle stays unchanged until the team owner saves the next billing setup."
                            : "Saving a payment method does not charge the card immediately. The subscription is finalized once the team owner completes setup."}
                    </div>
                  </div>
                ) : null}

                {pendingProrateSummary &&
                teamCapabilities.canManagePendingJoinProrate === true ? (
                  <div className="team-setup-payers__summary-card">
                    <div className="team-setup-payers__summary-kicker">Pending team join</div>
                    <div className="team-setup-payers__summary-plan">
                      {pendingProrateSummary.chargeRequired
                        ? "Complete one-time prorated join payment"
                        : "Complete pending team join"}
                    </div>
                    <div className="team-setup-payers__summary-copy">
                      {pendingProrateSummary.chargeRequired
                        ? "This team already has an active paid billing cycle. Complete the one-time prorated charge below to join the team for the remainder of the current cycle."
                        : "No card charge is required for this pending join. Complete the pending join below to become an active team member now."}
                    </div>

                    {!STRIPE_PUBLISHABLE_KEY && pendingProrateSummary.chargeRequired ? (
                      <div className="team-setup-payers__error">
                        Stripe is not configured. Missing REACT_APP_STRIPE_PUBLISHABLE_KEY.
                      </div>
                    ) : pendingProrateSummary.chargeRequired ? (
                      <Elements
                        stripe={stripePromise}
                        options={{
                          appearance: getStripeAppearance(isDarkMode),
                        }}
                      >
                        <PendingJoinProrateCard
                          teamId={teamId}
                          pendingProrateSummary={pendingProrateSummary}
                          onRefresh={refreshBilling}
                          teamBillingTimeZone={teamBillingTimeZone}
                        />
                      </Elements>
                    ) : (
                      <PendingJoinProrateNoChargeCard
                        teamId={teamId}
                        pendingProrateSummary={pendingProrateSummary}
                        onRefresh={refreshBilling}
                        teamBillingTimeZone={teamBillingTimeZone}
                      />
                    )}
                  </div>
                ) : null}

                <div className="team-setup-payers__summary-card">
                  <div className="team-setup-payers__summary-kicker">Your payment method</div>
                  <div className="team-setup-payers__summary-copy">
                    If you are one of the selected payers, connect your own card here. You cannot add or manage a card for another payer from this account.
                  </div>

                  <button
                    type="button"
                    className="team-setup-payers__button team-setup-payers__button--secondary"
                    onClick={() => navigate(buildBillingCenterPath(teamId))}
                  >
                    Manage Billing In Billing Center
                  </button>

                  {!STRIPE_PUBLISHABLE_KEY ? (
                    <div className="team-setup-payers__error">
                      Stripe is not configured. Missing REACT_APP_STRIPE_PUBLISHABLE_KEY.
                    </div>
                  ) : (
                    <Elements
                      stripe={stripePromise}
                      options={{
                        appearance: getStripeAppearance(isDarkMode),
                      }}
                    >
                      <PayerCardSetupForm
                        teamId={teamId}
                        billing={billingData}
                        onRefresh={refreshBilling}
                        currentUid={currentUid}
                      />
                    </Elements>
                  )}
                </div>
              </div>

              <aside className="team-setup-payers__sidebar">
                <div className="team-setup-payers__summary-card team-setup-payers__summary-card">
                  <div className="team-setup-payers__summary-kicker">
                    {finalizationCardKicker}
                  </div>

                  <div className="team-setup-payers__summary-plan">
                    {billingSummary?.planKey || billingData?.planKey || "No plan selected"}
                  </div>

                  <p className="team-setup-payers__summary-copy">
                    {finalizationCardCopy}
                  </p>

                  <div className="team-setup-payers__summary-grid">
                    <div className="team-setup-payers__summary-stat">
                      <span className="team-setup-payers__summary-stat-label">Payer Setup</span>
                      <strong className="team-setup-payers__summary-stat-value">
                        {hasSavedPayerSetup ? "Complete" : "Pending"}
                      </strong>
                    </div>

                    <div className="team-setup-payers__summary-stat">
                      <span className="team-setup-payers__summary-stat-label">Approvals</span>
                      <strong className="team-setup-payers__summary-stat-value">
                        {allApproved ? "Complete" : "Pending"}
                      </strong>
                    </div>

                    <div className="team-setup-payers__summary-stat">
                      <span className="team-setup-payers__summary-stat-label">Payment Methods</span>
                      <strong className="team-setup-payers__summary-stat-value">
                        {allReady ? "Complete" : "Pending"}
                      </strong>
                    </div>
                  </div>

                  {isCurrentSubscriptionLive ? (
                    <button
                      type="button"
                      className="team-setup-payers__button team-setup-payers__button--secondary"
                      onClick={() => navigate(buildBillingCenterPath(teamId))}
                    >
                      View Billing Overview
                    </button>
                  ) : canFinalizeBillingSetup ? (
                    <button
                      type="button"
                      className="team-setup-payers__button"
                      disabled={
                        !hasSavedPayerSetup ||
                        !allApproved ||
                        !allReady ||
                        completingSetup
                      }
                      onClick={handleOpenFinalizeReview}
                    >
                      {finalizeButtonLabel}
                    </button>
                  ) : (
                    <div className="team-setup-payers__summary-note">
                      {isBillingHoldRecovery
                        ? "Only the team owner can restart the team subscription once all selected payers are approved and payment-ready."
                        : hasPendingNextCycleChange
                        ? hasImmediatePendingChange
                          ? "Only the team owner can activate this approved upgrade once all selected payers are approved and payment-ready."
                          : "Only the team owner can save the next billing setup once all selected payers are approved and payment-ready."
                        : "Only the team owner can complete the subscription once all selected payers are approved and payment-ready."}
                    </div>
                  )}
                </div>
              </aside>
            </section>
          </div>
        </div>
      </div>
      {showApproveReview ? (
        <div
          className="team-setup-payers__review-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="team-setup-payers-approve-title"
        >
          <div className="team-setup-payers__review-modal">
            <div className="team-setup-payers__review-header">
              <div className="team-setup-payers__summary-kicker">Payer Confirmation</div>
              <h2
                id="team-setup-payers-approve-title"
                className="team-setup-payers__review-title"
              >
                Confirm your payer approval
              </h2>
              <p className="team-setup-payers__summary-copy">
                Review what you are approving for {teamName || "this team"} before your payer participation is marked as approved.
              </p>
            </div>

            <div className="team-setup-payers__review-grid">
              <div className="team-setup-payers__review-section">
                <div className="team-setup-payers__review-section-title">Team and billing scope</div>
                <div className="team-setup-payers__line-list">
                  <div className="team-setup-payers__line-item">
                    <span className="team-setup-payers__line-label">Team</span>
                    <span className="team-setup-payers__line-value">
                      {teamName || "Current team"}
                    </span>
                  </div>
                  <div className="team-setup-payers__line-item">
                    <span className="team-setup-payers__line-label">Plan</span>
                    <span className="team-setup-payers__line-value">
                      {billingBreakdown?.planLabel || "Not selected"}
                    </span>
                  </div>
                  <div className="team-setup-payers__line-item">
                    <span className="team-setup-payers__line-label">Approval scope</span>
                    <span className="team-setup-payers__line-value">
                      {hasPendingNextCycleChange
                        ? hasImmediatePendingChange
                          ? "Immediate upgrade approval"
                          : "Next billing cycle approval"
                        : "Current subscription setup"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="team-setup-payers__review-section">
                <div className="team-setup-payers__review-section-title">What this approval means</div>
                <div className="team-setup-payers__line-list">
                  <div className="team-setup-payers__line-item">
                    <span className="team-setup-payers__line-label">Your share</span>
                    <span className="team-setup-payers__line-value">
                      {formatMoneyAmount(
                        typeof billingBreakdown?.currentUserShare === "number"
                          ? billingBreakdown.currentUserShare
                          : billingBreakdown?.perPayerShare,
                        billingBreakdown?.displayCurrency || "CAD"
                      )} / month
                    </span>
                  </div>
                  <div className="team-setup-payers__line-item">
                    <span className="team-setup-payers__line-label">Split type</span>
                    <span className="team-setup-payers__line-value">
                      Equal split across selected payers before any payer-specific promo credits
                    </span>
                  </div>
                  <div className="team-setup-payers__line-item">
                    <span className="team-setup-payers__line-label">Important</span>
                    <span className="team-setup-payers__line-value">
                      This approves your participation in this team payment split. The team owner must still complete the team subscription step.
                    </span>
                  </div>
                </div>
              </div>

              <div className="team-setup-payers__review-section team-setup-payers__review-section--full">
                <div className="team-setup-payers__review-section-title">Legal acknowledgement</div>
                <label className="team-setup-payers__review-checkbox">
                  <input
                    type="checkbox"
                    checked={approveReviewAccepted}
                    onChange={(e) => setApproveReviewAccepted(e.target.checked)}
                    disabled={approvingPayer}
                  />
                  <span>
                    I confirm I am approving my own participation in this team payment split for {teamName || "this team"}. I have reviewed and accept the{" "}
                    <a
                      href="/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="team-setup-payers__billing-legal-link"
                    >
                      Terms of Service
                    </a>{" "}
                    and{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="team-setup-payers__billing-legal-link"
                    >
                      Privacy Policy
                    </a>
                    .
                  </span>
                </label>
              </div>
            </div>

            <div className="team-setup-payers__review-actions">
              <button
                type="button"
                className="team-setup-payers__button team-setup-payers__button--secondary"
                onClick={handleCloseApproveReview}
                disabled={approvingPayer}
              >
                Go Back
              </button>
              <button
                type="button"
                className="team-setup-payers__button"
                onClick={handleConfirmApprove}
                disabled={!approveReviewAccepted || approvingPayer}
              >
                {approvingPayer ? "Approving..." : "Confirm Approval"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {showFinalizeReview ? (
        <div
          className="team-setup-payers__review-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="team-setup-payers-review-title"
        >
          <div className="team-setup-payers__review-modal">
            <div className="team-setup-payers__review-header">
              <div className="team-setup-payers__summary-kicker">Final Confirmation</div>
              <h2
                id="team-setup-payers-review-title"
                className="team-setup-payers__review-title"
              >
                {isBillingHoldRecovery
                  ? "Review billing details before restarting access"
                  : hasPendingNextCycleChange
                  ? hasImmediatePendingChange
                    ? "Review billing upgrade before activation"
                    : "Review next-cycle billing details"
                  : "Review subscription details before activation"}
              </h2>
              <p className="team-setup-payers__summary-copy">
                {isBillingHoldRecovery
                  ? "Confirm the plan, payer split, timing, and legal acknowledgement before a new paid cycle is started and team access is restored."
                  : hasPendingNextCycleChange
                  ? hasImmediatePendingChange
                    ? "Confirm the upgraded plan, payer split, timing, and legal acknowledgement before the billing upgrade is activated for the team right away."
                    : "Confirm the queued plan, payer split, timing, and legal acknowledgement before this next billing setup is saved for the team."
                  : "Confirm the plan, payer split, trial status, and legal acknowledgement before the subscription is started for the team."}
              </p>
            </div>

            <div className="team-setup-payers__review-grid">
              <div className="team-setup-payers__review-section">
                <div className="team-setup-payers__review-section-title">Subscription</div>
                <div className="team-setup-payers__line-list">
                  <div className="team-setup-payers__line-item">
                    <span className="team-setup-payers__line-label">Plan</span>
                    <span className="team-setup-payers__line-value">
                      {billingBreakdown?.planLabel || "Not selected"}
                    </span>
                  </div>
                  <div className="team-setup-payers__line-item">
                    <span className="team-setup-payers__line-label">Billing interval</span>
                    <span className="team-setup-payers__line-value">
                      {billingBreakdown?.billingIntervalLabel || "Monthly"}
                    </span>
                  </div>
                  <div className="team-setup-payers__line-item">
                    <span className="team-setup-payers__line-label">Pricing mode</span>
                    <span className="team-setup-payers__line-value">
                      {billingBreakdown?.pricingModeLabel || "Standard"}
                    </span>
                  </div>
                  <div className="team-setup-payers__line-item">
                    <span className="team-setup-payers__line-label">Billable seats</span>
                    <span className="team-setup-payers__line-value">
                      {billingBreakdown?.billableSeatCount ?? "—"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="team-setup-payers__review-section">
                <div className="team-setup-payers__review-section-title">Cost breakdown</div>
                <div className="team-setup-payers__line-list">
                  <div className="team-setup-payers__line-item">
                    <span className="team-setup-payers__line-label">Cost per billable user</span>
                    <span className="team-setup-payers__line-value">
                      {formatMoneyAmount(
                        getPlanUnitPriceForCurrency(
                          getNormalizedString(billingSummary?.planKey) ||
                            getNormalizedString(billingData?.planKey),
                          SUBSCRIPTION_PLANS[
                            getNormalizedString(billingSummary?.planKey) ||
                              getNormalizedString(billingData?.planKey)
                          ],
                          billingBreakdown?.displayCurrency || "CAD",
                          isLaunchBasicPromoActive
                        ),
                        billingBreakdown?.displayCurrency || "CAD"
                      )} / month
                    </span>
                  </div>
                  <div className="team-setup-payers__line-item">
                    <span className="team-setup-payers__line-label">Plan subtotal</span>
                    <span className="team-setup-payers__line-value">
                      {formatMoneyAmount(
                        billingBreakdown?.planSubtotal,
                        billingBreakdown?.displayCurrency || "CAD"
                      )} / month
                    </span>
                  </div>
                  <div className="team-setup-payers__line-item">
                    <span className="team-setup-payers__line-label">Storage add-on</span>
                    <span className="team-setup-payers__line-value">
                      {billingBreakdown?.selectedStorageAddOn
                        ? formatMoneyAmount(
                            billingBreakdown?.storageSubtotal,
                            billingBreakdown?.displayCurrency || "CAD"
                          )
                        : "None"}
                    </span>
                  </div>
                  {billingBreakdown?.teamBillPromoDiscountMinor > 0 ? (
                    <div className="team-setup-payers__line-item">
                      <span className="team-setup-payers__line-label">Team discount</span>
                      <span className="team-setup-payers__line-value team-setup-payers__line-value--trial-eligible">
                        -{formatMoneyAmount(
                          billingBreakdown.teamBillPromoDiscountMinor / 100,
                          billingBreakdown.displayCurrency || "CAD"
                        )} / month
                      </span>
                    </div>
                  ) : null}
                  {billingBreakdown?.userBillPromoDiscountMinor > 0 ? (
                    <div className="team-setup-payers__line-item">
                      <span className="team-setup-payers__line-label">Payer-bill discounts</span>
                      <span className="team-setup-payers__line-value team-setup-payers__line-value--trial-eligible">
                        -{formatMoneyAmount(
                          billingBreakdown.userBillPromoDiscountMinor / 100,
                          billingBreakdown.displayCurrency || "CAD"
                        )} / month
                      </span>
                    </div>
                  ) : null}
                  <div className="team-setup-payers__line-item team-setup-payers__line-item--total">
                    <span className="team-setup-payers__line-label">Team total</span>
                    <span className="team-setup-payers__line-value">
                      {formatMoneyAmount(
                        billingBreakdown?.teamTotal,
                        billingBreakdown?.displayCurrency || "CAD"
                      )} / month
                    </span>
                  </div>
                  {billingBreakdown?.businessAbsorbedRoundingMinor > 0 ? (
                    <div className="team-setup-payers__line-item">
                      <span className="team-setup-payers__line-label">Business absorbed rounding</span>
                      <span className="team-setup-payers__line-value">
                        {formatMoneyAmount(
                          billingBreakdown.businessAbsorbedRoundingMinor / 100,
                          billingBreakdown.displayCurrency || "CAD"
                        )}
                      </span>
                    </div>
                  ) : null}
                </div>
              </div>

              {billingBreakdown?.appliedTeamPromos?.length > 0 ||
              billingBreakdown?.currentUserAppliedPromos?.length > 0 ? (
                <div className="team-setup-payers__review-section team-setup-payers__review-section--full">
                  <div className="team-setup-payers__review-section-title">Active discounts</div>
                  <div className="team-setup-payers__line-list">
                    {billingBreakdown?.appliedTeamPromos?.map((promo) => (
                      <div
                        key={`review-team-promo-${promo.code}`}
                        className="team-setup-payers__line-item"
                      >
                        <span className="team-setup-payers__line-label">
                          {promo.displayName || promo.code}
                        </span>
                        <span className="team-setup-payers__line-value">
                          {formatPromoBenefit(
                            promo,
                            billingBreakdown?.displayCurrency || "CAD"
                          )} • {formatPromoDetails(promo, teamBillingTimeZone)}
                        </span>
                      </div>
                    ))}
                    {billingBreakdown?.currentUserAppliedPromos?.map((promo) => (
                      <div
                        key={`review-user-promo-${promo.code}`}
                        className="team-setup-payers__line-item"
                      >
                        <span className="team-setup-payers__line-label">
                          {promo.displayName || promo.code}
                        </span>
                        <span className="team-setup-payers__line-value">
                          {formatPromoBenefit(
                            promo,
                            billingBreakdown?.displayCurrency || "CAD"
                          )} • {formatPromoDetails(promo, teamBillingTimeZone)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null}

              <div className="team-setup-payers__review-section team-setup-payers__review-section--full">
                <div className="team-setup-payers__review-section-title">Selected payers and approvals</div>
                <div className="team-setup-payers__review-payer-list">
                  {selectedPayerReviewRows.map((payer) => (
                    <div
                      key={payer.uid}
                      className="team-setup-payers__review-payer-card"
                    >
                      <div className="team-setup-payers__review-payer-header">
                        <strong>
                          {payer.displayName}
                          {payer.isCurrentUser ? " (You)" : ""}
                        </strong>
                        <span className="team-setup-payers__review-payer-price">
                          {formatMoneyAmount(
                            typeof billingBreakdown?.payerSharePreviewByUid?.[payer.uid]
                              ?.discountedRecurringAmountMinor === "number"
                              ? billingBreakdown.payerSharePreviewByUid[payer.uid]
                                  .discountedRecurringAmountMinor / 100
                              : billingBreakdown?.perPayerShare,
                            billingBreakdown?.displayCurrency || "CAD"
                          )} / month
                        </span>
                      </div>
                      <div className="team-setup-payers__summary-note">
                        Agreement status: {payer.approvalStatus}
                      </div>
                      <div className="team-setup-payers__summary-note">
                        Payment method: {payer.paymentMethodStatus}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="team-setup-payers__review-section team-setup-payers__review-section--full">
                <div className="team-setup-payers__review-section-title">Trial and billing timing</div>
                <div className="team-setup-payers__line-list">
                  <div className="team-setup-payers__line-item">
                    <span className="team-setup-payers__line-label">Free trial status</span>
                    <span className="team-setup-payers__line-value">
                      {isBillingHoldRecovery
                        ? "No new free trial is started. Paid access resumes when this new billing cycle is created."
                        : hasPendingNextCycleChange
                        ? hasImmediatePendingChange
                          ? "No new free trial is started. The approved upgrade applies immediately and only the prorated difference is charged until the next renewal date."
                          : "No new free trial is started. The queued update applies on the next billing date."
                        : shouldStartWithFreeTrial
                        ? `Included - starts ${formatBillingDate(reviewTrialStartsAt, teamBillingTimeZone)}`
                        : "Not included"}
                    </span>
                  </div>
                  {shouldStartWithFreeTrial && !hasPendingNextCycleChange ? (
                    <>
                      <div className="team-setup-payers__line-item">
                        <span className="team-setup-payers__line-label">Free trial end date</span>
                        <span className="team-setup-payers__line-value">
                          {formatBillingDate(reviewTrialEndsAt, teamBillingTimeZone)}
                        </span>
                      </div>
                      <div className="team-setup-payers__line-item">
                        <span className="team-setup-payers__line-label">First billing date</span>
                        <span className="team-setup-payers__line-value">
                          {formatBillingDate(reviewFirstBillingDate, teamBillingTimeZone)}
                        </span>
                      </div>
                    </>
                  ) : (
                    <div className="team-setup-payers__line-item">
                      <span className="team-setup-payers__line-label">
                        {isBillingHoldRecovery
                          ? "Restart date"
                          : hasPendingNextCycleChange
                            ? hasImmediatePendingChange
                              ? "Current renewal date"
                              : "Effective next billing date"
                            : "First billing date"}
                      </span>
                      <span className="team-setup-payers__line-value">
                        {formatBillingDate(reviewFirstBillingDate, teamBillingTimeZone)}
                      </span>
                    </div>
                  )}
                  {!hasPendingNextCycleChange && missingPhoneVerificationMembers.length > 0 ? (
                    <div className="team-setup-payers__note-box">
                      This team will start without a free trial because these team members have not completed phone verification:{" "}
                      {missingPhoneVerificationMembers
                        .map(({ uid, profile }) => profile?.displayName || uid)
                        .join(", ")}
                    </div>
                  ) : null}
                </div>
              </div>

              <div className="team-setup-payers__review-section team-setup-payers__review-section--full">
                <div className="team-setup-payers__review-section-title">Legal acknowledgement</div>
                <label className="team-setup-payers__review-checkbox">
                  <input
                    type="checkbox"
                    checked={finalizeReviewAccepted}
                    onChange={(e) => setFinalizeReviewAccepted(e.target.checked)}
                    disabled={completingSetup}
                  />
                  <span>
                    I confirm the subscription plan, pricing, payer split, free-trial status, and billing timing are correct for this team. I have reviewed and accept the{" "}
                    <a
                      href="/terms-of-service"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="team-setup-payers__billing-legal-link"
                    >
                      Terms of Service
                    </a>{" "}
                    and{" "}
                    <a
                      href="/privacy-policy"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="team-setup-payers__billing-legal-link"
                    >
                      Privacy Policy
                    </a>{" "}
                    {hasPendingNextCycleChange
                      ? isBillingHoldRecovery
                        ? "and want to restart this team subscription now."
                        : hasImmediatePendingChange
                          ? "and want to activate this billing upgrade for the team now."
                          : "and want to save this next billing setup for the team."
                      : "and want to start this subscription for the team now."}
                  </span>
                </label>
              </div>
            </div>

            <div className="team-setup-payers__review-actions">
              <button
                type="button"
                className="team-setup-payers__button team-setup-payers__button--secondary"
                onClick={handleCloseFinalizeReview}
                disabled={completingSetup}
              >
                Go Back
              </button>
              <button
                type="button"
                className="team-setup-payers__button"
                onClick={handleComplete}
                disabled={!finalizeReviewAccepted || completingSetup}
              >
                {completingSetup
                  ? isBillingHoldRecovery
                    ? "Restarting Subscription..."
                    : hasPendingNextCycleChange
                      ? hasImmediatePendingChange
                        ? "Activating Upgrade..."
                        : "Saving Billing Setup..."
                    : "Starting Subscription..."
                  : finalizeButtonLabel}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
    </TeamCenterFrame>
  );
};

export default TeamSetupPayers;
